import { parseSearchCalendarDate } from "./search/groupSearchResults";
import { clientMoveIn } from "../lib/clientMoveIn";
import { IconButton as DesignIconButton, Tooltip as DesignTooltip } from "@mui/material";
import { CloseOutlined as DesignClose } from "@mui/icons-material";
// src/components/ApartmentSearch.tsx
import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  startTransition,
  useTransition,
} from "react";
import { flushSync } from "react-dom";
import {
  Box,
  Card,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Checkbox,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Alert,
  CircularProgress,
  Stack,
  Autocomplete,
  Divider,
  Avatar,
  Collapse,
  alpha,
  useTheme,
  useMediaQuery,
  Switch,
  FormControlLabel,
  Drawer,
  Badge,
  FormGroup,
  Snackbar,
  Slider,
  Paper,
  OutlinedInput,
  ListItemText,
  Skeleton,
  CardMedia,
} from "@mui/material";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  Apartment as ApartmentIcon,
  Email as EmailIcon,
  Send as SendIcon,
  Save as SaveIcon,
  Check as CheckIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
  Tune as TuneIcon,
  Close as CloseIcon,
  Balcony as BalconyIcon,
  LocalLaundryService as LaundryIcon,
  Checkroom as ClosetIcon,
  Visibility as ViewIcon,
  Countertops as KitchenIslandIcon,
  Inventory2 as StorageIcon,
  Height as CeilingIcon,
  LocationOn as LocationIcon,
  BedOutlined as BedIcon,
  SquareFoot as SqftIcon,
  AttachMoney as MoneyIcon,
  ImageNotSupported as NoImageIcon,
  HomeWork as FloorPlanIcon,
  CalendarMonth as CalendarIcon,
  Warning as WarningIcon,
  SwapVert as SortIcon,
  BookmarkBorder as BookmarkIcon,
  DeleteOutline as DeleteIcon,
} from "@mui/icons-material";
import { gradients } from "../theme/theme";
import { activityService } from "../services/activityService";
import { supabase } from "../lib/supabase";
import { fetchWithAuth } from "../lib/api";
import { buildCapabilities } from "../lib/capabilities";
import { clientStore } from "../lib/clientStorage";
import {
  getPersistenceGeneration,
  hasPersistenceInvalidationFence,
  persistenceWriteAllowed,
  PERSISTENCE_LOCAL_CLEAR_KEY,
  readPersistenceEvent,
} from "../lib/persistenceIdentity";
import {
  readRegisteredValue,
  SEARCH_SCROLL_POLICY,
  serializeRegisteredValue,
} from "../lib/persistenceRegistry";
import {
  markPersistenceMutation,
  registerPersistenceFlushParticipant,
  type PersistenceFlushReceipt,
} from "../lib/persistenceReloadBarrier";
import { useSearchNeighborhoods, usePropertyInvalidation } from "../hooks/usePropertyData";
import SendListingsDialog from "./SendListingsDialog";
import ShortlistComposer, {
  type ShortlistSelection,
} from "./search/ShortlistComposer";
import { buildFreshShortlistSelections } from "./search/buildFreshShortlistSelections";
import { buildFreshListingSelections } from "./search/buildFreshListingSelections";
import ResultsMap, { MapPoint } from "./ResultsMap";
import { PropertiesSectionHeader } from "./PropertiesShell";
import { LoadingTopBar } from "./common/PageLoadingScaffold";
import { DEMO_SAVED_SEARCHES } from "../constants/demoSavedSearches";
import type {
  ListingUnit,
  GroupedProperty,
  ClientInfo,
} from "../services/emailService";
import {
  checkPropertyFreshness,
  type PropertyFreshnessItem,
} from "../services/scrapeGuardService";
import FreshnessBadge, { type FreshnessTier } from "./FreshnessBadge";
import {
  useDraftState,
  loadExactDraftSync,
  loadDraftSync,
  searchCriteriaFingerprint,
  type SearchCriteriaSnapshot,
  type SearchDraft,
} from "../hooks/useDraftState";
import {
  markSearchHistoryEntryOwned,
  parseAppNavigationState,
  readAppOwnedSearchHistoryBinding,
  replaceNavigationState,
  resolveSearchNavigationState,
} from "../lib/appNavigationState";
import { parseCalendarDate } from "../lib/calendarDate";
import {
  groupSearchResults,
  isAvailableByDate,
  type GroupedFloorPlan,
  type GroupedUnit,
  type SearchGroupedProperty,
  type SearchResult,
} from "./search/groupSearchResults";

// Types matching your API response

interface Client {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  max_budget?: number;
  min_bedrooms?: number;
  move_in_date?: string | null;
  move_in_timeline?: string;
}

interface SavedSearch {
  id: string;
  client_id: string;
  name: string;
  search_criteria: SearchCriteria;
  result_count: number;
  last_executed_at?: string;
  created_at: string;
  client_name?: string;
}

interface SearchCriteria {
  min_price?: number | null;
  max_price?: number | null;
  selected_bedrooms?: string[];
  min_sqft?: number | null;
  available_only?: boolean;
  amenities?: string[];
  neighborhoods?: string[];
  management_companies?: string[];
  price_per_sqft_max?: number | null;
  sort_by?: string;
  move_in_date?: string | null; // ISO date string YYYY-MM-DD
  near_address?: string;
  search_mode?: "manual_search" | "proximity_search";
  selected_unit_ids?: string[];
  selection_mode?: "floorplan" | "unit";
  selected_floor_plan_keys?: string[]; // "{property_id}-{model_name}-{beds}-{baths}-{sqft}"
}

// Helper to build a stable floor plan composite key
const buildFloorPlanKey = (
  propertyId: string,
  modelName: string,
  beds: number,
  baths: number,
  sqft: number | null
): string => `${propertyId}-${modelName}-${beds}-${baths}-${sqft ?? 0}`;

// Bedroom options for multi-select
const bedroomOptions = [
  { value: "0", label: "Studio", shortLabel: "Studio" },
  { value: "1", label: "1 Bedroom", shortLabel: "1 BD" },
  { value: "2", label: "2 Bedrooms", shortLabel: "2 BD" },
  { value: "3+", label: "3+ Bedrooms", shortLabel: "3+ BD" },
];

const sortOptions = [
  { value: "price", label: "Price: Low to High" },
  { value: "sqft", label: "Sq.Ft: High to Low" },
  { value: "price_per_sqft", label: "$/Sq.Ft: Low to High" },
];

const PROPERTY_RESULTS_FILTER_DEBOUNCE_MS = 120;

// Price slider configuration
const PRICE_MIN = 0;
const PRICE_MAX = 10000;
const PRICE_STEP = 50;


// Check if available NOW (today)
const isAvailableNow = (status: string, availabilityDate?: string): boolean => {
  return isAvailableByDate(status, availabilityDate, new Date());
};

// Format availability date for display
const formatAvailabilityDate = (
  availabilityDate?: string,
  targetDate?: Date
): string => {
  if (!availabilityDate) return "Available Now";

  try {
    const date = new Date(availabilityDate);
    const compareDate = targetDate || new Date();
    const dateOnly = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    );
    const compareDateOnly = new Date(
      compareDate.getFullYear(),
      compareDate.getMonth(),
      compareDate.getDate()
    );

    if (dateOnly <= compareDateOnly) return "Available Now";

    // Format as MM/DD
    return `Avail ${(date.getMonth() + 1).toString().padStart(2, "0")}/${date
      .getDate()
      .toString()
      .padStart(2, "0")}`;
  } catch {
    return "Available Now";
  }
};

const parseMoveInDate = parseSearchCalendarDate;

// Format date for HTML date input (YYYY-MM-DD) without timezone issues
const formatDateForInput = (date: Date | null): string => {
  if (!date) return "";
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Format date for display in filter
const formatDateForDisplay = (date: Date | null): string => {
  if (!date) return "Today";
  return `${(date.getMonth() + 1).toString().padStart(2, "0")}/${date
    .getDate()
    .toString()
    .padStart(2, "0")}/${date.getFullYear()}`;
};

// Helper functions
const formatNumber = (
  value: number | null | undefined,
  fallback: string = "N/A"
): string => {
  if (value === null || value === undefined || !isFinite(value))
    return fallback;
  return value.toLocaleString();
};

const formatPrice = (
  value: number | null | undefined,
  fallback: string = "N/A"
): string => {
  if (value === null || value === undefined || !isFinite(value))
    return fallback;
  return `$${value.toLocaleString()}`;
};

const formatPriceShort = (value: number): string => {
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(1)}k`;
  }
  return `$${value}`;
};

// Move-in date input — text format MM/DD/YYYY with manual parsing + native picker
interface MoveInDateInputProps {
  value: Date | null;
  onChange: (date: Date | null) => void;
}

const MoveInDateInput: React.FC<MoveInDateInputProps> = ({ value, onChange }) => {
  const formatForDisplay = (d: Date | null): string => {
    if (!d) return "";
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  };

  const formatForNative = (d: Date | null): string => {
    if (!d) return "";
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${d.getFullYear()}-${mm}-${dd}`;
  };

  const [inputValue, setInputValue] = useState(formatForDisplay(value));
  const hiddenDateRef = useRef<HTMLInputElement>(null);

  // Sync external changes (e.g. auto-fill from move-in date)
  useEffect(() => {
    setInputValue(formatForDisplay(value));
  }, [value]);

  const parseAndCommit = (raw: string) => {
    const trimmed = raw.trim();
    if (!trimmed) {
      onChange(null);
      return;
    }
    const match = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (!match) return;
    const month = parseInt(match[1], 10) - 1;
    const day = parseInt(match[2], 10);
    const year = parseInt(match[3], 10);
    if (year < 1900 || year > 2100) return;
    const parsed = new Date(year, month, day);
    if (isNaN(parsed.getTime())) return;
    onChange(parsed);
  };

  const openNativePicker = () => {
    const el = hiddenDateRef.current;
    if (!el) return;
    if (typeof (el as any).showPicker === "function") {
      try {
        (el as any).showPicker();
        return;
      } catch {
        // fall through to focus+click
      }
    }
    el.focus();
    el.click();
  };

  return (
    <Box sx={{ position: "relative", display: "inline-flex", width: "100%" }}>
      <TextField
        fullWidth
        size="small"
        placeholder="MM/DD/YYYY"
        value={inputValue}
        onChange={(e) => {
          const cleaned = e.target.value.replace(/[^\d/]/g, "");
          setInputValue(cleaned);
        }}
        onBlur={() => parseAndCommit(inputValue)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            parseAndCommit(inputValue);
            (e.target as HTMLInputElement).blur();
          }
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start" sx={{ ml: 0.5, mr: 0 }}>
              <IconButton
                size="small"
                onClick={openNativePicker}
                sx={{ p: 0.25 }}
                aria-label="Open date picker"
              >
                <CalendarIcon
                  fontSize="small"
                  sx={{ color: "text.secondary" }}
                />
              </IconButton>
            </InputAdornment>
          ),
          endAdornment: value && (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={() => {
                  onChange(null);
                  setInputValue("");
                }}
                sx={{ p: 0.25 }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{
          minWidth: { xs: 0, sm: 180 },
          "& .MuiOutlinedInput-root": {
            borderRadius: 2,
            height: 36,
          },
          "& .MuiInputBase-input": {
            py: 0.75,
            fontSize: "0.875rem",
          },
        }}
      />
      {/* Hidden native date input — opened via showPicker() from the calendar icon */}
      <input
        ref={hiddenDateRef}
        type="date"
        value={formatForNative(value)}
        onChange={(e) => {
          if (e.target.value) {
            const parts = e.target.value.split("-");
            if (parts.length === 3) {
              const year = parseInt(parts[0], 10);
              const month = parseInt(parts[1], 10) - 1;
              const day = parseInt(parts[2], 10);
              onChange(new Date(year, month, day));
            }
          } else {
            onChange(null);
          }
        }}
        style={{
          position: "absolute",
          opacity: 0,
          pointerEvents: "none",
          width: 1,
          height: 1,
          left: 8,
          bottom: 0,
        }}
        tabIndex={-1}
        aria-hidden="true"
      />
    </Box>
  );
};

// Image component with fallback
interface PropertyImageProps {
  src?: string;
  alt: string;
  height?: number | string;
  borderRadius?: number | string;
}

const PropertyImage: React.FC<PropertyImageProps> = ({
  src,
  alt,
  height = 180,
  borderRadius = 2,
}) => {
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();

  if (!src || error) {
    return (
      <Box
        sx={{
          height,
          borderRadius,
          backgroundColor: alpha(theme.palette.primary.main, 0.08),
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          gap: 1,
        }}
      >
        <NoImageIcon
          sx={{ fontSize: 40, color: alpha(theme.palette.text.secondary, 0.4) }}
        />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ opacity: 0.6 }}
        >
          No image available
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{ position: "relative", height, borderRadius, overflow: "hidden" }}
    >
      {loading && (
        <Skeleton
          variant="rectangular"
          sx={{ position: "absolute", inset: 0, borderRadius }}
        />
      )}
      <CardMedia
        component="img"
        image={src}
        alt={alt}
        onError={() => setError(true)}
        onLoad={() => setLoading(false)}
        sx={{
          height: "100%",
          width: "100%",
          objectFit: "cover",
          borderRadius,
          opacity: loading ? 0 : 1,
          transition: "opacity 0.3s ease",
        }}
      />
    </Box>
  );
};

// Floor Plan Image Thumbnail
interface FloorPlanThumbnailProps {
  src?: string;
  alt: string;
  onClick?: () => void;
  loading?: boolean;
}

const FloorPlanThumbnail: React.FC<FloorPlanThumbnailProps> = ({
  src,
  alt,
  onClick,
  loading = false,
}) => {
  const [error, setError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const theme = useTheme();

  useEffect(() => {
    setError(false);
    setImageLoaded(false);
  }, [src]);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onClick) {
      onClick();
    }
  };

  if (!src || error) {
    return (
      <Tooltip title="No floor plan available">
        <Box
          sx={{
            width: 60,
            height: 60,
            borderRadius: 1.5,
            backgroundColor: alpha(theme.palette.grey[500], 0.1),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <FloorPlanIcon
            sx={{ color: alpha(theme.palette.text.secondary, 0.3) }}
          />
        </Box>
      </Tooltip>
    );
  }

  return (
    <Tooltip title="View floor plan">
      <Box
        onClick={onClick ? handleClick : undefined}
        sx={{
          position: "relative",
          width: 60,
          height: 60,
          borderRadius: 1.5,
          overflow: "hidden",
          cursor: onClick ? "pointer" : "default",
          border: `1px solid ${theme.palette.divider}`,
          transition: "all 0.2s ease",
          "&:hover": onClick
            ? {
                transform: "scale(1.05)",
                boxShadow: theme.shadows[4],
              }
            : {},
        }}
      >
        <img
          src={src}
          alt={alt}
          onError={() => setError(true)}
          onLoad={() => setImageLoaded(true)}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            opacity: imageLoaded ? 1 : 0.35,
            transition: "opacity 0.2s ease",
          }}
        />
        {(!imageLoaded || loading) && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: alpha(theme.palette.background.paper, 0.72),
              backdropFilter: "blur(2px)",
            }}
          >
            <CircularProgress
              size={22}
              thickness={5}
              sx={{ color: theme.palette.common.white }}
            />
          </Box>
        )}
      </Box>
    </Tooltip>
  );
};

interface PropertyResultsSearchFieldProps {
  appliedQuery: string;
  onApplyQuery: (nextValue: string) => void;
  onUpdatingChange: (updating: boolean) => void;
}

const PropertyResultsSearchField: React.FC<PropertyResultsSearchFieldProps> = ({
  appliedQuery,
  onApplyQuery,
  onUpdatingChange,
}) => {
  const [draftQuery, setDraftQuery] = useState(appliedQuery);
  const [isPending, startFilterTransition] = useTransition();

  useEffect(() => {
    setDraftQuery(appliedQuery);
  }, [appliedQuery]);

  useEffect(() => {
    if (draftQuery === appliedQuery) return;

    const timeoutId = window.setTimeout(() => {
      startFilterTransition(() => {
        onApplyQuery(draftQuery);
      });
    }, PROPERTY_RESULTS_FILTER_DEBOUNCE_MS);

    return () => window.clearTimeout(timeoutId);
  }, [draftQuery, appliedQuery, onApplyQuery, startFilterTransition]);

  const isUpdating = draftQuery !== appliedQuery || isPending;

  useEffect(() => {
    onUpdatingChange(isUpdating);
  }, [isUpdating, onUpdatingChange]);

  return (
    <TextField
      size="small"
      value={draftQuery}
      onChange={(event) => setDraftQuery(event.target.value)}
      placeholder="Search these properties"
      sx={{
        flex: { xs: "1 1 100%", md: "1 1 320px" },
        minWidth: { xs: "100%", md: 260 },
        maxWidth: { xs: "100%", xl: 420 },
        ml: { xs: 0, md: 1 },
        "& .MuiOutlinedInput-root": {
          height: 40,
        },
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon fontSize="small" />
          </InputAdornment>
        ),
        endAdornment: isUpdating ? (
          <InputAdornment position="end">
            <CircularProgress
              size={16}
              thickness={5}
              sx={{ color: "primary.light" }}
            />
          </InputAdornment>
        ) : draftQuery ? (
          <InputAdornment position="end">
            <IconButton
              edge="end"
              size="small"
              onClick={() => setDraftQuery("")}
              aria-label="Clear property search"
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ) : undefined,
      }}
    />
  );
};

// Property Row Component
interface PropertyRowProps {
  property: SearchGroupedProperty;
  expanded: boolean;
  expanding: boolean;
  onToggle: () => void;
  onExpandEntered: () => void;
  expandedFloorPlans: Set<string>;
  expandingFloorPlanKey: string | null;
  onToggleFloorPlan: (key: string) => void;
  onFloorPlanExpandEntered: (key: string) => void;
  selectedUnits: Set<string>;
  onToggleUnit: (
    unitKey: string,
    unit: GroupedUnit,
    floorPlan: GroupedFloorPlan,
    property: SearchGroupedProperty
  ) => void;
  onViewFloorPlan: (
    imageUrl: string,
    modelName: string,
    floorPlanKey: string,
    units: GroupedUnit[]
  ) => void;
  openingFloorPlanKey: string | null;
  moveInDate: Date | null;
  freshness?: PropertyFreshnessItem | null;
  freshnessLoading: boolean;
  formatFreshnessDate: (dateStr: string | null | undefined) => string;
  isAdmin: boolean;
  selectionMode: "floorplan" | "unit";
  selectedFloorPlans: Set<string>;
  onToggleFloorPlanSelection: (
    floorPlan: GroupedFloorPlan,
    property: SearchGroupedProperty
  ) => void;
}

const PropertyRow: React.FC<PropertyRowProps> = ({
  property,
  expanded,
  expanding,
  onToggle,
  onExpandEntered,
  expandedFloorPlans,
  expandingFloorPlanKey,
  onToggleFloorPlan,
  onFloorPlanExpandEntered,
  selectedUnits,
  onToggleUnit,
  onViewFloorPlan,
  openingFloorPlanKey,
  moveInDate,
  freshness,
  freshnessLoading,
  formatFreshnessDate,
  isAdmin,
  selectionMode,
  selectedFloorPlans,
  onToggleFloorPlanSelection,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const targetDate = moveInDate || new Date();
  const today = new Date(); // Always use today for status chip display
  const freshnessTier = freshness?.freshness_tier ?? property.freshness_tier;
  const freshnessNote = freshness?.serve_note ?? property.freshness_note;
  const freshnessBadge = freshness?.badge ?? property.freshness_badge;
  const servePricing = freshness?.serve_pricing ?? property.serve_pricing;

  const priceRangeLabel = () => {
    const min = property.price_range.min;
    const max = property.price_range.max;
    if (!isFinite(min) || !isFinite(max)) return "Price varies";
    if (min === max) return formatPrice(min);
    return `${formatPrice(min)} - ${formatPrice(max)}`;
  };

  const bedRangeLabel = () => {
    const min = property.bed_range.min;
    const max = property.bed_range.max;
    if (!isFinite(min) || !isFinite(max)) return "Beds vary";
    if (min === max) return min === 0 ? "Studio" : `${min} bed`;
    return min === 0 ? `Studio - ${max} beds` : `${min}-${max} beds`;
  };

  return (
    <Card
      data-property-id={property.property_id}
      sx={{
        mb: 2,
        overflow: "hidden",
        borderRadius: 3,
        transition: "all 0.3s ease",
        border: expanded
          ? `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
          : `1px solid ${theme.palette.divider}`,
        "&:hover": {
          boxShadow: theme.shadows[4],
        },
      }}
    >
      {/* Property Header with Image */}
      <Box
        onClick={onToggle}
        data-tour="property-row-header"
        sx={{
          display: "flex",
          cursor: "pointer",
          height: { xs: 88, sm: 96 },
          backgroundColor: expanded
            ? alpha(theme.palette.primary.main, 0.02)
            : "transparent",
          transition: "background-color 0.2s ease",
          "&:hover": {
            backgroundColor: alpha(theme.palette.primary.main, 0.04),
          },
        }}
      >
        {/* Property Image */}
        <Box
          sx={{
            width: { xs: 80, sm: 140, md: 170 },
            height: "100%",
            flexShrink: 0,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <PropertyImage
            src={property.s3_image_url}
            alt={property.property_name}
            height="100%"
            borderRadius={0}
          />
          {/* Overlay gradient */}
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: "50%",
              background:
                "linear-gradient(to top, rgba(0,0,0,0.4) 0%, transparent 100%)",
              display: { xs: "none", sm: "block" },
            }}
          />
        </Box>

        {/* Property Info */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            p: { xs: 1, sm: 1.5 },
            display: "flex",
            alignItems: "center",
            gap: 1,
            overflow: "hidden",
          }}
        >
          {!isMobile && (
            <IconButton size="small" sx={{ mr: 1 }}>
              {expanding ? (
                <CircularProgress
                  size={18}
                  thickness={5}
                  sx={{ color: theme.palette.primary.light }}
                />
              ) : expanded ? (
                <KeyboardArrowDownIcon />
              ) : (
                <KeyboardArrowRightIcon />
              )}
            </IconButton>
          )}

          <Box flex={1} sx={{ minWidth: 0 }}>
            {isMobile ? (
              <>
                <Typography
                  variant="body1"
                  fontWeight={700}
                  noWrap
                  sx={{ lineHeight: 1.2 }}
                >
                  {property.property_name}
                </Typography>
                <Box sx={{ mt: 0.25 }}>
                  <FreshnessBadge
                    tier={freshnessTier}
                    note={freshnessNote}
                    badge={freshnessBadge}
                  />
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  sx={{
                    display: "block",
                    fontVariantNumeric: "tabular-nums",
                    mt: 0.25,
                  }}
                >
                  {servePricing ? priceRangeLabel() : "Verify with property"}
                  {servePricing && property.available_units > 0 && (
                    <>
                      {" \u00b7 "}
                      <Box
                        component="span"
                        sx={{ color: "success.main", fontWeight: 600 }}
                      >
                        {property.available_units} avail
                      </Box>
                    </>
                  )}
                </Typography>
              </>
            ) : (
              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                sx={{ flexWrap: "wrap", rowGap: 0.5 }}
              >
                <Typography
                  variant="h6"
                  fontWeight={700}
                  sx={{ fontSize: "1.15rem", lineHeight: 1.3 }}
                >
                  {property.property_name}
                </Typography>
                <Stack
                  direction="row"
                  spacing={0.75}
                  sx={{ flexWrap: "wrap", gap: 0.75 }}
                >
                  <FreshnessBadge
                    tier={freshnessTier}
                    note={freshnessNote}
                    badge={freshnessBadge}
                  />
                  {servePricing && (
                    <>
                      <Chip
                        size="small"
                        label={`${property.available_units} available`}
                        color="success"
                        sx={{ fontWeight: 600, borderRadius: 1.5, height: 26 }}
                      />
                      <Chip
                        size="small"
                        icon={<MoneyIcon sx={{ fontSize: 14 }} />}
                        label={priceRangeLabel()}
                        variant="outlined"
                        sx={{ borderRadius: 1.5, height: 26 }}
                      />
                      <Chip
                        size="small"
                        icon={<BedIcon sx={{ fontSize: 14 }} />}
                        label={bedRangeLabel()}
                        variant="outlined"
                        sx={{ borderRadius: 1.5, height: 26 }}
                      />
                      <Chip
                        size="small"
                        label={`${property.floor_plans.length} floor plan${property.floor_plans.length === 1 ? "" : "s"}`}
                        variant="outlined"
                        sx={{
                          borderRadius: 1.5,
                          height: 26,
                          display: { xs: "none", md: "flex" },
                        }}
                      />
                    </>
                  )}
                </Stack>
              </Stack>
            )}
          </Box>

          {isMobile && (
            <IconButton size="small" sx={{ flexShrink: 0, ml: 0.5 }}>
              {expanding ? (
                <CircularProgress
                  size={18}
                  thickness={5}
                  sx={{ color: theme.palette.primary.light }}
                />
              ) : expanded ? (
                <KeyboardArrowDownIcon />
              ) : (
                <KeyboardArrowRightIcon />
              )}
            </IconButton>
          )}

          <Box
            sx={{ textAlign: "right", display: { xs: "none", sm: "block" }, minWidth: 130 }}
          >
            {freshnessLoading ? null : (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5 }}>
                <Typography
                  variant="caption"
                  color={servePricing ? "text.secondary" : "error.main"}
                >
                  {servePricing
                    ? freshnessTier === "aging"
                      ? freshnessNote
                      : `Data from: ${formatFreshnessDate(freshness?.last_scraped ?? property.last_verified_at)}`
                    : "Pricing & availability hidden"}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Expanded Floor Plans */}
      <Collapse in={expanded} onEntered={onExpandEntered}>
        <Box sx={{ px: 2, pb: 2, pt: 1 }}>
          {property.floor_plans.map((floorPlan) => {
            const fpKey = `${property.property_id}-${floorPlan.model_name}`;
            const fpExpanded = expandedFloorPlans.has(fpKey);
            const fpExpanding = expandingFloorPlanKey === fpKey;
            const fpSelectionKey = buildFloorPlanKey(
              property.property_id,
              floorPlan.model_name,
              floorPlan.bedrooms,
              floorPlan.bathrooms,
              floorPlan.square_feet
            );
            const isFpSelected = selectedFloorPlans.has(fpSelectionKey);

            const floorPlanPriceLabel = () => {
              const min = floorPlan.min_price;
              const max = floorPlan.max_price;
              if (!isFinite(min)) return "Price varies";
              if (min === max || !isFinite(max)) return `~${formatPrice(min)}`;
              return `${formatPrice(min)} – ${formatPrice(max)}`;
            };

            const isFloorPlanMode = selectionMode === "floorplan";
            const isUnavailable = floorPlan.available_count === 0;
            const isDisabled = isFloorPlanMode && isUnavailable;

            return (
              <Box key={fpKey} sx={{ mt: 1 }}>
                <Box
                  data-tour="floor-plan-row"
                  onClick={() => {
                    if (isDisabled) return;
                    isFloorPlanMode
                      ? onToggleFloorPlanSelection(floorPlan, property)
                      : onToggleFloorPlan(fpKey);
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    p: 1,
                    cursor: isDisabled ? "default" : "pointer",
                    opacity: isDisabled ? 0.5 : 1,
                    backgroundColor: isFloorPlanMode
                      ? isFpSelected
                        ? alpha(theme.palette.primary.main, 0.1)
                        : alpha(theme.palette.background.default, 0.5)
                      : fpExpanded
                      ? alpha(theme.palette.primary.main, 0.06)
                      : alpha(theme.palette.background.default, 0.5),
                    borderRadius: !isFloorPlanMode && fpExpanded ? "12px 12px 0 0" : 3,
                    border: `1px solid ${
                      isFloorPlanMode
                        ? isFpSelected
                          ? alpha(theme.palette.primary.main, 0.4)
                          : theme.palette.divider
                        : fpExpanded
                        ? alpha(theme.palette.primary.main, 0.2)
                        : theme.palette.divider
                    }`,
                    borderBottom: !isFloorPlanMode && fpExpanded ? "none" : undefined,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: alpha(theme.palette.primary.main, 0.08),
                    },
                  }}
                >
                  {/* Floor plan mode: checkbox | Unit mode: expand icon */}
                  {isFloorPlanMode ? (
                    <Checkbox
                      data-tour="floor-plan-checkbox"
                      checked={isFpSelected}
                      disabled={isDisabled}
                      sx={{ p: 0.5, mr: 0.5 }}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => !isDisabled && onToggleFloorPlanSelection(floorPlan, property)}
                    />
                  ) : (
                    <IconButton size="small" sx={{ mx: isMobile ? 0.5 : 1 }}>
                      {fpExpanding ? (
                        <CircularProgress
                          size={18}
                          thickness={5}
                          sx={{ color: theme.palette.primary.light }}
                        />
                      ) : fpExpanded ? (
                        <ExpandMoreIcon />
                      ) : (
                        <KeyboardArrowRightIcon />
                      )}
                    </IconButton>
                  )}

                  {/* Floor plan thumbnail — always visible (clickable to view) */}
                  {!isMobile && (
                    <Box
                      onClick={(e) => e.stopPropagation()}
                      sx={{ display: "flex", alignItems: "center", mr: 1.5 }}
                    >
                      <FloorPlanThumbnail
                        src={floorPlan.image_url}
                        alt={floorPlan.model_name}
                        loading={openingFloorPlanKey === fpKey}
                        onClick={
                          floorPlan.image_url
                            ? () => {
                                onViewFloorPlan(
                                  floorPlan.image_url!,
                                  floorPlan.model_name,
                                  fpKey,
                                  floorPlan.units
                                );
                              }
                            : undefined
                        }
                      />
                    </Box>
                  )}

                  <Box flex={1} sx={{ minWidth: 0 }}>
                    {isMobile ? (
                      <>
                        <Typography
                          variant="body1"
                          fontWeight={600}
                          noWrap
                          sx={{ lineHeight: 1.2 }}
                        >
                          {floorPlan.model_name || "Unknown"}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          noWrap
                          sx={{ display: "block", fontSize: "0.75rem" }}
                        >
                          {floorPlan.bedrooms === 0
                            ? "Studio"
                            : `${floorPlan.bedrooms}bd`}
                          {" · "}
                          {floorPlan.bathrooms ?? "?"}ba
                          {" · "}
                          {formatNumber(floorPlan.square_feet)}sf
                        </Typography>
                      </>
                    ) : (
                      <Stack
                        direction="row"
                        spacing={1.5}
                        alignItems="center"
                        sx={{ flexWrap: "wrap", rowGap: 0.25 }}
                      >
                        <Typography variant="subtitle1" fontWeight={600}>
                          {floorPlan.model_name || "Unknown"}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ whiteSpace: "nowrap" }}
                        >
                          {floorPlan.bedrooms === 0
                            ? "Studio"
                            : `${floorPlan.bedrooms}bd`}
                          {" · "}
                          {floorPlan.bathrooms ?? "?"}ba
                          {" · "}
                          {formatNumber(floorPlan.square_feet)}
                          {" sq ft"}
                        </Typography>
                      </Stack>
                    )}
                  </Box>

                  {isMobile ? (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-end",
                        gap: 0.5,
                        flexShrink: 0,
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        color="primary"
                        sx={{ lineHeight: 1.2 }}
                      >
                        {floorPlanPriceLabel()}
                      </Typography>
                      <Chip
                        size="small"
                        label={`${floorPlan.available_count} avail`}
                        color={
                          floorPlan.available_count > 0 ? "success" : "default"
                        }
                        sx={{
                          fontWeight: 600,
                          borderRadius: 1.5,
                          height: 20,
                          fontSize: "0.7rem",
                          "& .MuiChip-label": { px: 0.75 },
                        }}
                      />
                    </Box>
                  ) : (
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Chip
                        size="small"
                        label={`${floorPlan.available_count} available`}
                        color={
                          floorPlan.available_count > 0 ? "success" : "default"
                        }
                        sx={{ fontWeight: 600, borderRadius: 1.5 }}
                      />
                      <Typography
                        variant="body1"
                        fontWeight={700}
                        color="primary"
                        sx={{ minWidth: 80, textAlign: "right" }}
                      >
                        {floorPlanPriceLabel()}
                      </Typography>
                    </Stack>
                  )}
                </Box>

                {/* Unit table — only in unit mode */}
                {!isFloorPlanMode && (
                <Collapse
                  in={fpExpanded}
                  onEntered={() => onFloorPlanExpandEntered(fpKey)}
                >
                  <TableContainer
                    sx={{
                      border: `1px solid ${theme.palette.divider}`,
                      borderTop: "none",
                      borderRadius: "0 0 12px 12px",
                      overflow: "hidden",
                    }}
                  >
                    <Table size="small">
                      <TableHead>
                        <TableRow
                          sx={{
                            backgroundColor: alpha(
                              theme.palette.primary.main,
                              0.04
                            ),
                          }}
                        >
                          <TableCell padding="checkbox" />
                          <TableCell sx={{ fontWeight: 600 }}>Unit #</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            Price
                          </TableCell>
                          {!isMobile && (
                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                              $/Sq.Ft
                            </TableCell>
                          )}
                          <TableCell align="center" sx={{ fontWeight: 600 }}>
                            Status
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {floorPlan.units.map((unit) => {
                          // Use unique_id for selection - fixes "private" unit issue
                          const unitKey = unit.unique_id;
                          const isSelected = selectedUnits.has(unitKey);

                          return (
                            <TableRow
                              key={unitKey}
                              hover
                              selected={isSelected}
                              onClick={() =>
                                onToggleUnit(unitKey, unit, floorPlan, property)
                              }
                              sx={{
                                cursor: "pointer",
                                transition: "background-color 0.15s ease",
                                "&.Mui-selected": {
                                  backgroundColor: alpha(
                                    theme.palette.primary.main,
                                    0.12
                                  ),
                                },
                              }}
                            >
                              <TableCell padding="checkbox">
                                <Checkbox
                                  checked={isSelected}
                                  sx={{ p: 0.5 }}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight={500}>
                                  {unit.unit_number || "N/A"}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography
                                  fontWeight={700}
                                  color="primary.main"
                                >
                                  {formatPrice(unit.current_price)}
                                </Typography>
                              </TableCell>
                              {!isMobile && (
                                <TableCell align="right">
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {unit.price_per_sqft
                                      ? `$${unit.price_per_sqft.toFixed(2)}`
                                      : "N/A"}
                                  </Typography>
                                </TableCell>
                              )}
                              <TableCell align="center">
                                <Chip
                                  size="small"
                                  label={
                                    unit.status === "available"
                                      ? formatAvailabilityDate(
                                          unit.available_date,
                                          today
                                        )
                                      : unit.status || "unknown"
                                  }
                                  color={
                                    isAvailableByDate(
                                      unit.status,
                                      unit.available_date,
                                      today
                                    )
                                      ? "success"
                                      : unit.status === "available"
                                      ? "info" // Future availability - blue is more readable
                                      : "default"
                                  }
                                  sx={{
                                    textTransform:
                                      unit.status === "available"
                                        ? "none"
                                        : "capitalize",
                                    fontWeight: 500,
                                    borderRadius: 1,
                                    height: 24,
                                  }}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Collapse>
                )}
              </Box>
            );
          })}
        </Box>
      </Collapse>
    </Card>
  );
};

interface ApartmentSearchProps {
  userId: string | null;
  initialClientId?: string | null;
  initialNeighborhoods?: string[] | null;
  initialSearchCriteria?: SearchCriteria | null;
  isAdmin?: boolean;
  isDemo?: boolean;
  userRole?: "admin" | "manager" | "agent" | "demo";
  onNavigate?: (page: string) => void;
}

// Amenity options are the UNIT features actually captured in properties.attributes.scope
// ("all" = in every unit, "select" = in some). The old building-amenity vocab
// (pool/gym/parking/…) mapped to columns the scraper leaves null, so the filter
// matched nothing. Values below are the exact scope keys the backend filters on.
const amenityOptions = [
  { value: "in_unit_laundry", label: "In-Unit Laundry", icon: <LaundryIcon fontSize="small" /> },
  { value: "walk_in_closet", label: "Walk-in Closet", icon: <ClosetIcon fontSize="small" /> },
  { value: "view", label: "Great View", icon: <ViewIcon fontSize="small" /> },
  { value: "kitchen_island", label: "Kitchen Island", icon: <KitchenIslandIcon fontSize="small" /> },
  { value: "private_outdoor", label: "Private Outdoor", icon: <BalconyIcon fontSize="small" /> },
  { value: "in_unit_storage", label: "In-Unit Storage", icon: <StorageIcon fontSize="small" /> },
  { value: "ceiling", label: "High Ceilings", icon: <CeilingIcon fontSize="small" /> },
];

// Drop unknown/legacy amenity values (saved searches from the old building-amenity
// vocab — pool/gym/… — no longer map to any scope key; restoring them raw would
// send dead params and silently return zero results).
const AMENITY_VALUES = new Set(amenityOptions.map((o) => o.value));
const sanitizeAmenities = (list?: string[] | null): string[] =>
  (list ?? []).filter((a) => AMENITY_VALUES.has(a));

const ApartmentSearch: React.FC<ApartmentSearchProps> = ({
  userId,
  initialClientId,
  initialNeighborhoods,
  initialSearchCriteria,
  isAdmin = false,
  isDemo = false,
  userRole = "agent",
  onNavigate,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const capabilities = React.useMemo(
    () => buildCapabilities({ role: userRole, isDemo, isMobile }),
    [userRole, isDemo, isMobile]
  );
  const canUseUnitMode = capabilities.canUseUnitMode;

  // Davis feedback 2026-04-25: leaving the search/research page and coming
  // back drops the user back at the top. Persist scroll position to
  // registered durable storage on scroll (rAF-coalesced), restore on mount. Keyed by
  // initialClientId so different client research sessions stay separate.
  React.useEffect(() => {
    if (!userId) return;
    const storageKey = `scroll:${userId}:search:${initialClientId || "all"}`;
    let ownerGeneration = getPersistenceGeneration(userId);
    const raw = clientStore.getItem(storageKey);
    const initialKeyGeneration = clientStore.generation(storageKey);
    const legacyScroll = raw !== null ? Number(raw) : Number.NaN;
    const canMigrateLegacyScroll =
      !hasPersistenceInvalidationFence(userId, ownerGeneration);
    let restoredY = Number.isFinite(legacyScroll) && legacyScroll >= 0 &&
      canMigrateLegacyScroll
      ? legacyScroll
      : readRegisteredValue<number>(
          raw,
          SEARCH_SCROLL_POLICY,
          Date.now(),
          {
            ownerGeneration,
            keyGeneration: initialKeyGeneration,
          }
        );
    if (
      Number.isFinite(legacyScroll) && legacyScroll >= 0 &&
      canMigrateLegacyScroll
    ) {
      const migrated = serializeRegisteredValue(
        legacyScroll,
        SEARCH_SCROLL_POLICY,
        Date.now(),
        { ownerGeneration, keyGeneration: initialKeyGeneration }
      );
      if (migrated) {
        clientStore.setItemGuarded(storageKey, migrated, initialKeyGeneration);
      }
    }
    if (raw && restoredY === null) clientStore.removeItem(storageKey);
    let keyGeneration = clientStore.generation(storageKey);
    let clearInProgress = false;
    let restoreFrame: number | null = null;
    if (restoredY !== null && restoredY > 0) {
        const restoreTop = restoredY;
        const restoreOwnerGeneration = ownerGeneration;
        // requestAnimationFrame so React paints content before we scroll;
        // otherwise we'd land on a stale layout target.
        restoreFrame = requestAnimationFrame(() => {
          restoreFrame = null;
          if (
            clearInProgress ||
            !persistenceWriteAllowed(userId, restoreOwnerGeneration)
          ) return;
          window.scrollTo({ top: restoreTop, behavior: "auto" });
        });
    }
    const cancelRestore = () => {
      if (restoreFrame !== null) cancelAnimationFrame(restoreFrame);
      restoreFrame = null;
    };
    let pendingFrame: number | null = null;
    let scrollDirty = false;
    const persistScroll = (): boolean => {
      if (
        clearInProgress ||
        !persistenceWriteAllowed(userId, ownerGeneration)
      ) return !scrollDirty;
      if (window.scrollY <= 0) {
        if (clientStore.getItem(storageKey) !== null) {
          const removed = clientStore.removeItem(storageKey);
          keyGeneration = clientStore.generation(storageKey);
          if (!removed) return false;
        }
        scrollDirty = false;
        return clientStore.getDurableItem(storageKey) === null;
      }
      const stored = serializeRegisteredValue(
        window.scrollY,
        SEARCH_SCROLL_POLICY,
        Date.now(),
        { ownerGeneration, keyGeneration }
      );
      if (stored) {
        const durable = clientStore.setItemGuarded(
          storageKey,
          stored,
          keyGeneration
        ) && clientStore.getDurableItem(storageKey) === stored;
        if (durable) scrollDirty = false;
        return durable;
      }
      return false;
    };
    const unsubscribe = clientStore.subscribe((changedKey, value) => {
      if (changedKey === PERSISTENCE_LOCAL_CLEAR_KEY && value) {
        const event = readPersistenceEvent(value, "local-clear");
        if (event?.userId === userId) {
          cancelRestore();
          ownerGeneration = event.generation;
          clearInProgress = clientStore.getItem(storageKey) !== null;
          if (!clearInProgress) {
            keyGeneration = clientStore.generation(storageKey);
            window.scrollTo({ top: 0, behavior: "auto" });
          }
        }
        return;
      }
      if (changedKey === storageKey && value === null) {
        cancelRestore();
        keyGeneration = clientStore.generation(storageKey);
        clearInProgress = false;
        window.scrollTo({ top: 0, behavior: "auto" });
      }
    });
    const onScroll = () => {
      scrollDirty = true;
      markPersistenceMutation();
      if (pendingFrame !== null) return;
      pendingFrame = requestAnimationFrame(() => {
        persistScroll();
        pendingFrame = null;
      });
    };
    const flush = (): PersistenceFlushReceipt => {
      if (pendingFrame !== null) cancelAnimationFrame(pendingFrame);
      pendingFrame = null;
      const wasDirty = scrollDirty;
      if (!wasDirty) {
        return {
          participant: `search:scroll:${initialClientId ?? "none"}`,
          dirty: false,
          durable: true,
        };
      }
      const durable = persistScroll();
      return {
        participant: `search:scroll:${initialClientId ?? "none"}`,
        dirty: wasDirty,
        durable,
        reason: durable ? undefined : "scroll-write-unverified",
      };
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") flush();
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", flush);
    const unregisterFlush = registerPersistenceFlushParticipant(
      `search:scroll:${initialClientId ?? "none"}`,
      flush
    );
    return () => {
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", flush);
      unregisterFlush();
      cancelRestore();
      unsubscribe();
      flush();
    };
  }, [initialClientId, userId]);

  // Counter for generating unique IDs

  // ── Synchronous draft hydration ──────────────────────────────────
  // Read any existing session draft BEFORE state is initialized so every
  // useState lazy initializer below can seed itself from restored values.
  // This eliminates the "default → restored" flash when a user switches
  // Chrome tabs and returns.
  //
  // When initialSearchCriteria is present (dashboard-driven nav), criteria
  // take priority and we deliberately skip sync draft.
  const [initialUrlSearch] = useState(
    () => parseAppNavigationState(window.location.search).search
  );
  const [initialSearchHistoryBinding] = useState(() =>
    readAppOwnedSearchHistoryBinding(window.history.state)
  );
  const [syncDraft] = useState<SearchDraft | null>(() => {
    if (initialSearchCriteria) return null;
    const draft = initialSearchHistoryBinding
      ? loadExactDraftSync(initialSearchHistoryBinding.clientId, userId)
      : initialUrlSearch
        ? null
        : loadDraftSync(initialClientId ?? null, userId);
    if (!draft || !initialUrlSearch) return draft;

    // A matching URL is not enough to prove it belongs to this private local
    // workspace: a pasted/shared link can legitimately contain the same
    // filters while targeting different selections and no client. Search's
    // URL writer marks its own history entry, and that marker survives an
    // honest reload/Back restoration but is absent from a pasted/new-tab URL.
    if (!initialSearchHistoryBinding) return null;

    // Search writes public criteria to the URL while it is mounted. That URL
    // must not make the private same-user draft (client, proximity criterion,
    // compose text, selections) inert on the next reload. Merge when public
    // criteria agree; a differing shared URL starts clean and leaves the old
    // client-bound draft untouched under its own key.
    const restored = resolveSearchNavigationState(initialUrlSearch);
    const urlFilters: SearchDraft["filters"] = {
      priceRange: restored.price ?? [PRICE_MIN, PRICE_MAX],
      selectedBedrooms: restored.bedrooms,
      minSqft: restored.minSqft ?? "",
      availableOnly: restored.availableOnly ?? true,
      moveInDate: restored.moveInDate,
      sortBy: restored.sortBy ?? "price",
      selectedNeighborhoods: restored.neighborhoods,
      selectedAmenities: sanitizeAmenities(restored.amenities),
      selectedManagement: restored.management,
      maxPricePerSqft: restored.maxPricePerSqft ?? "",
      noFloorPlanImage: restored.noFloorPlanImage,
    };
    const urlFingerprint = searchCriteriaFingerprint({
      nearAddress: "",
      filters: urlFilters,
    });
    const draftPublicFingerprint = searchCriteriaFingerprint({
      nearAddress: "",
      filters: draft.filters,
    });
    if (urlFingerprint === draftPublicFingerprint) return draft;
    // A differing/shared URL is a clean, unbound workspace. Reusing the prior
    // client or compose could send new-criteria listings to the wrong person.
    // Leave the old draft untouched under its client key for later recovery.
    return null;
  });
  const sd = syncDraft; // shorthand for initializers

  // Search filters
  const [priceRange, setPriceRange] = useState<[number, number]>(() =>
    initialUrlSearch?.price ?? sd?.filters.priceRange ?? [PRICE_MIN, PRICE_MAX]
  );
  const [selectedBedrooms, setSelectedBedrooms] = useState<string[]>(
    () => initialUrlSearch?.bedrooms ?? sd?.filters.selectedBedrooms ?? []
  );
  const [minSqft, setMinSqft] = useState<number | "">(
    () => initialUrlSearch?.minSqft ?? sd?.filters.minSqft ?? ""
  );
  const [availableOnly, setAvailableOnly] = useState(
    () => initialUrlSearch?.availableOnly ?? sd?.filters.availableOnly ?? true
  );
  const [moveInDate, setMoveInDate] = useState<Date | null>(() =>
    initialUrlSearch?.moveInDate ? parseSearchCalendarDate(initialUrlSearch.moveInDate) : sd?.filters.moveInDate ? parseSearchCalendarDate(sd.filters.moveInDate) : null
  );
  const [sortBy, setSortBy] = useState(() => initialUrlSearch?.sortBy ?? sd?.filters.sortBy ?? "price");

  // Advanced filters
  const [selectedNeighborhoods, setSelectedNeighborhoods] = useState<string[]>(
    () => initialUrlSearch?.neighborhoods ?? sd?.filters.selectedNeighborhoods ?? []
  );
  const [selectedAmenities, setSelectedAmenities] = useState<string[]>(
    () => sanitizeAmenities(initialUrlSearch?.amenities ?? sd?.filters.selectedAmenities)
  );
  const [selectedManagement, setSelectedManagement] = useState<string[]>(
    () => initialUrlSearch?.management ?? sd?.filters.selectedManagement ?? []
  );
  const [maxPricePerSqft, setMaxPricePerSqft] = useState<number | "">(
    () => initialUrlSearch?.maxPricePerSqft ?? sd?.filters.maxPricePerSqft ?? ""
  );
  const [noFloorPlanImage, setNoFloorPlanImage] = useState(
    () => initialUrlSearch?.noFloorPlanImage ?? sd?.filters.noFloorPlanImage ?? false
  );
  const { data: availableNeighborhoods = [] } = useSearchNeighborhoods();
  const { invalidateProperty: invalidateCachedProperty } = usePropertyInvalidation();
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // UI state
  const [loading, setLoading] = useState(false);
  const searchRequestEpochRef = useRef(0);
  const [error, setError] = useState<string | null>(null);
  const [expandedProperties, setExpandedProperties] = useState<Set<string>>(
    new Set()
  );
  const [expandedFloorPlans, setExpandedFloorPlans] = useState<Set<string>>(
    new Set()
  );
  const [expandingPropertyId, setExpandingPropertyId] = useState<string | null>(null);
  const [expandingFloorPlanKey, setExpandingFloorPlanKey] = useState<string | null>(null);
  const [openingFloorPlanKey, setOpeningFloorPlanKey] = useState<string | null>(null);

  // Results — seeded from sync draft cache so we don't flash an empty grid
  const [searchResults, setSearchResults] = useState<SearchResult[]>(() => {
    const cached = sd?.cachedSearchResults;
    return Array.isArray(cached) && cached.length > 0
      ? (cached as SearchResult[])
      : [];
  });
  const executedResultFingerprintRef = useRef<string | null>(
    sd?.cachedResultCriteriaFingerprint ?? null
  );
  const [groupedResults, setGroupedResults] = useState<SearchGroupedProperty[]>(
    []
  );
  // "Close to X" is private owner-scoped Search work. Persist it with the
  // draft (never in the shareable URL), otherwise a tab discard can repaint
  // geo-ranked rows while silently losing the criterion that produced them.
  const [nearAddress, setNearAddress] = useState(() => sd?.nearAddress ?? "");
  const [geoNotice, setGeoNotice] = useState<string | null>(null);
  const [showMap, setShowMap] = useState(false);
  // Restore the persisted mode when the current role can use it. Unit keys and
  // an open compose dialog are one coherent recovery unit; forcing floorplan
  // here would hide the restored selection and reopen a blank dialog.
  const [selectionMode, setSelectionMode] = useState<"floorplan" | "unit">(
    () => initialUrlSearch?.selectionMode ??
      (sd?.selectionMode === "unit" && !canUseUnitMode
        ? "floorplan"
        : sd?.selectionMode) ??
      "floorplan"
  );
  const [selectedUnits, setSelectedUnits] = useState<
    Map<
      string,
      {
        unit: GroupedUnit;
        floorPlan: GroupedFloorPlan;
        property: SearchGroupedProperty;
      }
    >
  >(new Map());
  const [selectedFloorPlans, setSelectedFloorPlans] = useState<
    Map<
      string,
      { floorPlan: GroupedFloorPlan; property: SearchGroupedProperty }
    >
  >(new Map());
  const shortlistSelections = useMemo<ShortlistSelection[]>(
    () => buildFreshShortlistSelections(
      selectionMode,
      selectedFloorPlans,
      selectedUnits,
      moveInDate || undefined
    ),
    [selectionMode, selectedFloorPlans, selectedUnits, moveInDate]
  );
  const [pendingFloorPlanRestorations, setPendingFloorPlanRestorations] = useState<string[] | null>(() => {
    const mode = initialUrlSearch?.selectionMode ?? (sd?.selectionMode === "unit" && !canUseUnitMode ? "floorplan" : sd?.selectionMode);
    const keys = initialUrlSearch?.selectedKeys ?? sd?.selectedFloorPlanKeys ?? [];
    if (mode === "floorplan" && keys.length > 0) {
      return keys;
    }
    return null;
  });

  // Client & saved searches — selectedClient seeded with a stub built from
  // the sync draft so the Autocomplete paints the client name immediately
  // instead of flashing empty while the async clients list loads. The real
  // Client object binds in the clients-loaded effect and replaces the stub.
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClient, setSelectedClient] = useState<Client | null>(() => {
    if (!sd || !sd.clientId || !sd.clientFirstName || !sd.clientLastName) {
      return null;
    }
    return {
      id: sd.clientId,
      first_name: sd.clientFirstName,
      last_name: sd.clientLastName,
    } as Client;
  });
  const [clientSavedSearches, setClientSavedSearches] = useState<SavedSearch[]>(
    []
  );
  const [clientsLoaded, setClientsLoaded] = useState(false);

  // Saved search unit restoration
  const [pendingUnitRestorations, setPendingUnitRestorations] = useState<string[] | null>(() => {
    const mode = initialUrlSearch?.selectionMode ?? (sd?.selectionMode === "unit" && !canUseUnitMode ? "floorplan" : sd?.selectionMode);
    const keys = initialUrlSearch?.selectedKeys ?? sd?.selectedUnitKeys ?? [];
    if (mode === "unit" && keys.length > 0) {
      return keys;
    }
    return null;
  });
  // Refs mirror pending state so handleSearch (memoized) always sees current values
  const pendingUnitRestorationsRef = useRef<string[] | null>(null);
  const pendingFloorPlanRestorationsRef = useRef<string[] | null>(null);
  useEffect(() => { pendingUnitRestorationsRef.current = pendingUnitRestorations; }, [pendingUnitRestorations]);
  useEffect(() => { pendingFloorPlanRestorationsRef.current = pendingFloorPlanRestorations; }, [pendingFloorPlanRestorations]);

  // Dialogs & drawers
  const [savedSearchesOpen, setSavedSearchesOpen] = useState(false);
  const [allSavedSearches, setAllSavedSearches] = useState<
    SavedSearch[]
  >([]);
  const [allSearchesLoaded, setAllSearchesLoaded] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [freshnessData, setFreshnessData] = useState<Map<string, PropertyFreshnessItem>>(new Map());
  const [freshnessLoading, setFreshnessLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const floorPlanImageRequestRef = useRef(0);
  const [searchName, setSearchName] = useState("");
  const [appliedPropertySearchQuery, setAppliedPropertySearchQuery] = useState("");
  const [isPropertyResultsUpdating, setIsPropertyResultsUpdating] = useState(false);
  const [floorPlanDialog, setFloorPlanDialog] = useState<{
    open: boolean;
    imageUrl: string;
    modelName: string;
    error: boolean;
    units: GroupedUnit[];
  }>({ open: false, imageUrl: "", modelName: "", error: false, units: [] });

  // Sync-open the Send-Listings dialog on first paint when persisted, so
  // the modal is already up on frame 1 — hiding the background reassembly
  // sequence (selections rehydrating, groupedResults recomputing) behind
  // the modal's own backdrop. `listingsToSend` starts empty and fills in
  // once selections rehydrate via the pending-reopen effect below; the
  // dialog shows a compact skeleton for the listings area while empty.
  const [sendListingsDialogOpen, setSendListingsDialogOpen] = useState<boolean>(
    () => Boolean(sd?.sendDialog?.open)
  );
  const [listingsToSend, setListingsToSend] = useState<ListingUnit[]>([]);
  // Persisted compose text for the Send-Listings dialog — seeded from
  // sessionStorage on mount so a tab discard mid-compose doesn't wipe it.
  const [sendDialogDraft, setSendDialogDraft] = useState<{
    subject: string;
    message: string;
    recipientEmail: string;
  } | null>(() => {
    const d = sd?.sendDialog;
    if (!d) return null;
    return {
      subject: d.subject,
      message: d.message,
      recipientEmail: d.recipientEmail,
    };
  });
  // If the Send-Listings dialog was open when the tab was left, reopen it
  // after selections finish rehydrating. One-shot.
  const pendingSendListingsReopen = useRef<boolean>(
    Boolean(sd?.sendDialog?.open)
  );
  const initialSearchAppliedRef = useRef(false);
  const [queuedCriteriaSearch, setQueuedCriteriaSearch] = useState(false);
  const [hasExecutedSearch, setHasExecutedSearch] = useState(
    () => Boolean(sd?.hasExecutedSearch)
  );
  const [draftClientIdToHydrate, setDraftClientIdToHydrate] = useState<string | null>(
    () => sd?.clientId ?? null
  );
  // If sync hydration already populated state from sessionStorage, mark
  // restore complete on first render so the hydration gate in the render
  // tree never paints a default-state frame.
  const [draftRestoreComplete, setDraftRestoreComplete] = useState(() => sd !== null);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info";
  }>({ open: false, message: "", severity: "success" });

  // ── Draft persistence (survives browser tab switch) ──────────
  const {
    loadDraft,
    saveDraft,
    saveResultSnapshot,
    discardResultSnapshot,
    clearDraft,
    clearRevision,
    acknowledgeLocalClear,
  } = useDraftState(userId);
  const draftHydrated = useRef(false);        // true after restore attempt completes
  const draftRestored = useRef(Boolean(sd));  // true if a real draft was loaded (not just "no draft found")
  const hadPersistableWorkRef = useRef(Boolean(sd));
  const lastPersistedDraftClientIdRef = useRef<string | null>(sd?.clientId ?? null);
  // Sync hydration already produced restored values synchronously, so the
  // save-effect gate can open on the very first commit — no async gap.
  const [draftSaveGate, setDraftSaveGate] = useState(() => sd !== null);
  const draftSuppressNextSave = useRef(false); // skip one save pass (used by clearFilters)
  const bootstrapEpochRef = useRef(0);
  const postClearAwaitingUserRef = useRef(false);
  const unownedUrlAwaitingUserRef = useRef(
    Boolean(initialUrlSearch && !initialSearchHistoryBinding)
  );
  const searchEntryNeedsBindingRef = useRef(!initialSearchHistoryBinding);
  const activeDraftClientIdRef = useRef<string | null>(
    initialSearchHistoryBinding?.clientId ?? sd?.clientId ?? initialClientId ?? null
  );
  const resumePersistenceAfterUserAction = useCallback(() => {
    if (
      !postClearAwaitingUserRef.current &&
      !unownedUrlAwaitingUserRef.current &&
      !searchEntryNeedsBindingRef.current
    ) return;
    postClearAwaitingUserRef.current = false;
    markSearchHistoryEntryOwned(activeDraftClientIdRef.current);
    searchEntryNeedsBindingRef.current = false;
    unownedUrlAwaitingUserRef.current = false;
    setDraftSaveGate(true);
  }, []);

  // Rehydrate from draft on mount (only if no initialSearchCriteria)
  useEffect(() => {
    if (draftHydrated.current) return;
    draftHydrated.current = true;
    const hydrationEpoch = bootstrapEpochRef.current;

    // initialSearchCriteria takes priority — don't restore draft.
    // Save gate opens when initialSearchAppliedRef is set (see criteria effect).
    if (initialSearchCriteria) {
      setDraftRestoreComplete(true);
      return;
    }
    if (initialUrlSearch) {
      setDraftSaveGate(!unownedUrlAwaitingUserRef.current);
      setDraftRestoreComplete(true);
      // A same-criteria private draft may safely supply its client, compose,
      // proximity criterion and fingerprinted result. A differing/shared URL
      // was sanitized synchronously above and must execute its visible state.
      // A URL is criteria passed in (shared link, Back/Forward) — it still runs (PKT-0844).
      if (!sd?.cachedSearchResults?.length) setQueuedCriteriaSearch(true);
      return;
    }

    // If the sync read at render-time already hydrated state from
    // sessionStorage, skip the async re-read. All setState calls below
    // would just re-apply the same values (causing churn) — and the
    // sync path has already set draftRestoreComplete + draftSaveGate.
    if (sd) {
      // Restored criteria without a cached result: a client passed in from a
      // client record ("Run Search") still re-runs; a plain tab click stays an
      // unrun form until the user presses Search (PKT-0844).
      if (sd.hasExecutedSearch && (!sd.cachedSearchResults || sd.cachedSearchResults.length === 0)) {
        if (initialClientId) setQueuedCriteriaSearch(true);
        else setHasExecutedSearch(false);
      }
      return;
    }

    const clientId = initialClientId ?? null;
    loadDraft(clientId).then((draft) => {
      if (
        bootstrapEpochRef.current !== hydrationEpoch ||
        postClearAwaitingUserRef.current
      ) return;
      if (!draft) {
        setDraftSaveGate(true);
        setDraftRestoreComplete(true);
        return;
      }

      // Mark that real draft data was restored
      draftRestored.current = true;
      const hasCachedResults =
        Array.isArray(draft.cachedSearchResults) && draft.cachedSearchResults.length > 0;
      // Without a cached result, only a client passed in from a client record
      // re-runs; a plain tab click restores an unrun form (PKT-0844).
      const rerunForClient = Boolean(draft.hasExecutedSearch) && !hasCachedResults && Boolean(initialClientId);
      setHasExecutedSearch(Boolean(draft.hasExecutedSearch) && (hasCachedResults || rerunForClient));
      setDraftClientIdToHydrate(draft.clientId ?? null);

      // Restore filters
      const f = draft.filters;
      setPriceRange(f.priceRange);
      setSelectedBedrooms(f.selectedBedrooms);
      setMinSqft(f.minSqft);
      setAvailableOnly(f.availableOnly);
      setMoveInDate(f.moveInDate ? parseSearchCalendarDate(f.moveInDate) : null);
      setSortBy(f.sortBy);
      setSelectedNeighborhoods(f.selectedNeighborhoods);
      setSelectedAmenities(sanitizeAmenities(f.selectedAmenities));
      setSelectedManagement(f.selectedManagement);
      setMaxPricePerSqft(f.maxPricePerSqft);
      setNoFloorPlanImage(f.noFloorPlanImage);
      setNearAddress(draft.nearAddress ?? "");

      // Restore selection mode + queue pending restorations
      const mode = draft.selectionMode === "unit" && !canUseUnitMode ? "floorplan" : draft.selectionMode;
      setSelectionMode(mode);
      if (mode === "floorplan" && draft.selectedFloorPlanKeys.length > 0) {
        setPendingFloorPlanRestorations(draft.selectedFloorPlanKeys);
      } else if (mode === "unit" && draft.selectedUnitKeys.length > 0) {
        setPendingUnitRestorations(draft.selectedUnitKeys);
      }

      // Restore previous result set immediately to avoid a visible "blank reset"
      // on return from tab discard/reload.
      if (Array.isArray(draft.cachedSearchResults) && draft.cachedSearchResults.length > 0) {
        setSearchResults(draft.cachedSearchResults as SearchResult[]);
        executedResultFingerprintRef.current =
          draft.cachedResultCriteriaFingerprint ??
          searchCriteriaFingerprint(draft);
      }

      // Gate opens via setState — ensures restored values are committed
      // before the save effect can run (React batches this with the above)
      setDraftSaveGate(true);

      if (rerunForClient) setQueuedCriteriaSearch(true);
      setDraftRestoreComplete(true);
    });
  }, []); // Run once on mount

  // If draft was loaded before clients arrived, re-bind selected client once
  // the client list is available.
  useEffect(() => {
    if (postClearAwaitingUserRef.current) return;
    if (!draftClientIdToHydrate) return;
    if (!clientsLoaded || clients.length === 0) return;
    const client = clients.find((c) => c.id === draftClientIdToHydrate);
    if (client) {
      setSelectedClient(client);
    }
    setDraftClientIdToHydrate(null);
  }, [draftClientIdToHydrate, clientsLoaded, clients]);

  const currentCriteriaFingerprint = useMemo(() => searchCriteriaFingerprint({
    nearAddress: nearAddress.slice(0, 500),
    filters: {
      priceRange,
      selectedBedrooms,
      minSqft,
      availableOnly,
      moveInDate: moveInDate?.toISOString().split("T")[0] ?? null,
      sortBy,
      selectedNeighborhoods,
      selectedAmenities,
      selectedManagement,
      maxPricePerSqft,
      noFloorPlanImage,
    },
  }), [
    availableOnly, maxPricePerSqft, minSqft, moveInDate, nearAddress,
    noFloorPlanImage, priceRange, selectedAmenities, selectedBedrooms,
    selectedManagement, selectedNeighborhoods, sortBy,
  ]);

  // Visible rows belong to the criteria that actually produced them, never
  // merely to whatever fields happen to be on screen now. A layout effect
  // removes a stale pairing before paint, while compose/selection-only edits
  // do not touch the large result snapshot.
  React.useLayoutEffect(() => {
    const executedFingerprint = executedResultFingerprintRef.current;
    if (!executedFingerprint || executedFingerprint === currentCriteriaFingerprint) {
      return;
    }
    executedResultFingerprintRef.current = null;
    searchRequestEpochRef.current += 1;
    setLoading(false);
    setSearchResults([]);
    setGroupedResults([]);
    setHasExecutedSearch(false);
    setGeoNotice(null);
    discardResultSnapshot(
      selectedClient?.id ?? draftClientIdToHydrate ?? initialClientId ?? null
    );
  }, [
    currentCriteriaFingerprint, discardResultSnapshot, draftClientIdToHydrate,
    initialClientId, selectedClient,
  ]);

  // Save draft on filter/selection changes (gated — no saves until hydration complete)
  useEffect(() => {
    if (!draftSaveGate) return;

    // Skip one save pass if suppressed (e.g., after Clear Filters)
    if (draftSuppressNextSave.current) {
      draftSuppressNextSave.current = false;
      return;
    }

    // Don't persist all-default / no-selection state (avoids recreating
    // an empty draft immediately after Clear Filters)
    const hasSelections = selectedFloorPlans.size > 0 || selectedUnits.size > 0;
    const hasNonDefaultFilters =
      selectedBedrooms.length > 0 ||
      selectedNeighborhoods.length > 0 ||
      selectedAmenities.length > 0 ||
      selectedManagement.length > 0 ||
      minSqft !== "" ||
      maxPricePerSqft !== "" ||
      !availableOnly ||
      moveInDate !== null ||
      noFloorPlanImage ||
      priceRange[0] !== PRICE_MIN ||
      priceRange[1] !== PRICE_MAX;
    const hasPrivateProximityCriterion = nearAddress.trim().length > 0;

    if (
      !hasExecutedSearch && !hasSelections && !hasNonDefaultFilters &&
      !hasPrivateProximityCriterion
    ) {
      if (hadPersistableWorkRef.current) {
        clearDraft(lastPersistedDraftClientIdRef.current);
        executedResultFingerprintRef.current = null;
        hadPersistableWorkRef.current = false;
      }
      return;
    }

    const draftClientId =
      selectedClient?.id ?? draftClientIdToHydrate ?? initialClientId ?? null;
    activeDraftClientIdRef.current = draftClientId;
    lastPersistedDraftClientIdRef.current = draftClientId;
    hadPersistableWorkRef.current = true;
    markSearchHistoryEntryOwned(draftClientId);
    searchEntryNeedsBindingRef.current = false;
    unownedUrlAwaitingUserRef.current = false;

    saveDraft({
      clientId: draftClientId,
      // Persist display name so the Autocomplete can render synchronously
      // on next mount via the stub pattern (see useState initializer above).
      clientFirstName: selectedClient?.first_name,
      clientLastName: selectedClient?.last_name,
      nearAddress: nearAddress.slice(0, 500),
      hasExecutedSearch,
      filters: {
        priceRange,
        selectedBedrooms,
        minSqft,
        availableOnly,
        moveInDate: moveInDate?.toISOString().split("T")[0] ?? null,
        sortBy,
        selectedNeighborhoods,
        selectedAmenities,
        selectedManagement,
        maxPricePerSqft,
        noFloorPlanImage,
      },
      selectionMode,
      selectedFloorPlanKeys: Array.from(selectedFloorPlans.keys()),
      selectedUnitKeys: Array.from(selectedUnits.keys()),
      // Send-Listings dialog (the big modal with subject/message/recipient
      // + HTML preview). Persisted so a tab discard mid-compose doesn't
      // wipe the user's writing. Only written when the dialog is open or
      // there's a non-empty draft.
      sendDialog:
        sendListingsDialogOpen ||
        (sendDialogDraft &&
          (sendDialogDraft.subject.length > 0 ||
            sendDialogDraft.message.length > 0 ||
            sendDialogDraft.recipientEmail.length > 0))
          ? {
              open: sendListingsDialogOpen,
              subject: sendDialogDraft?.subject ?? "",
              message: sendDialogDraft?.message ?? "",
              recipientEmail: sendDialogDraft?.recipientEmail ?? "",
            }
          : undefined,
    });
  }, [
    draftSaveGate,
    hasExecutedSearch,
    priceRange, selectedBedrooms, minSqft, availableOnly, moveInDate,
    sortBy, selectedNeighborhoods, selectedAmenities, selectedManagement,
    maxPricePerSqft, noFloorPlanImage, nearAddress, selectionMode,
    selectedFloorPlans, selectedUnits, selectedClient, initialClientId,
    sendListingsDialogOpen, sendDialogDraft,
  ]);

  // Shareable, non-sensitive search context. Free-form addresses, client
  // names, email drafts, cached results and recipient fields never enter URL.
  useEffect(() => {
    const selectedKeys = selectionMode === "unit"
      ? (selectedUnits.size ? Array.from(selectedUnits.keys()) : pendingUnitRestorations ?? [])
      : (selectedFloorPlans.size ? Array.from(selectedFloorPlans.keys()) : pendingFloorPlanRestorations ?? []);
    const isCanonicalDefault =
      priceRange[0] === PRICE_MIN && priceRange[1] === PRICE_MAX &&
      selectedBedrooms.length === 0 && minSqft === "" && availableOnly &&
      moveInDate === null && sortBy === "price" &&
      selectedNeighborhoods.length === 0 && selectedAmenities.length === 0 &&
      selectedManagement.length === 0 && maxPricePerSqft === "" &&
      !noFloorPlanImage && selectionMode === "floorplan" &&
      selectedKeys.length === 0;
    replaceNavigationState({ search: isCanonicalDefault ? null : {
      price: priceRange,
      bedrooms: selectedBedrooms,
      minSqft: minSqft === "" ? null : minSqft,
      availableOnly,
      moveInDate: moveInDate?.toISOString().split("T")[0] ?? null,
      sortBy,
      neighborhoods: selectedNeighborhoods,
      amenities: selectedAmenities,
      management: selectedManagement,
      maxPricePerSqft: maxPricePerSqft === "" ? null : maxPricePerSqft,
      noFloorPlanImage,
      selectionMode,
      selectedKeys,
    }});
  }, [priceRange, selectedBedrooms, minSqft, availableOnly, moveInDate, sortBy,
    selectedNeighborhoods, selectedAmenities, selectedManagement, maxPricePerSqft,
    noFloorPlanImage, selectionMode, selectedUnits, selectedFloorPlans,
    pendingUnitRestorations, pendingFloorPlanRestorations]);

  useEffect(() => {
    const onPopState = () => {
      const destinationBinding = readAppOwnedSearchHistoryBinding(
        window.history.state
      );
      const destinationSearch = parseAppNavigationState(
        window.location.search
      ).search;
      activeDraftClientIdRef.current = destinationBinding?.clientId ?? null;
      hadPersistableWorkRef.current = false;
      lastPersistedDraftClientIdRef.current = null;
      searchEntryNeedsBindingRef.current = true;
      unownedUrlAwaitingUserRef.current = Boolean(
        destinationSearch && !destinationBinding
      );
      setDraftSaveGate(false);
      searchRequestEpochRef.current += 1;
      executedResultFingerprintRef.current = null;
      discardResultSnapshot(
        selectedClient?.id ?? draftClientIdToHydrate ?? initialClientId ?? null
      );
      const restored = resolveSearchNavigationState(
        destinationSearch
      );
      setPriceRange(restored.price ?? [PRICE_MIN, PRICE_MAX]);
      setSelectedBedrooms(restored.bedrooms);
      setMinSqft(restored.minSqft ?? "");
      setAvailableOnly(restored.availableOnly ?? true);
      setMoveInDate(restored.moveInDate ? parseSearchCalendarDate(restored.moveInDate) : null);
      setSortBy(restored.sortBy ?? "price");
      setSelectedNeighborhoods(restored.neighborhoods);
      setSelectedAmenities(sanitizeAmenities(restored.amenities));
      setSelectedManagement(restored.management);
      setMaxPricePerSqft(restored.maxPricePerSqft ?? "");
      setNoFloorPlanImage(restored.noFloorPlanImage);
      setNearAddress("");
      setGeoNotice(null);
      setLoading(false);
      setError(null);
      setSearchResults([]);
      setGroupedResults([]);
      setHasExecutedSearch(false);
      setExpandedProperties(new Set());
      setExpandedFloorPlans(new Set());
      setExpandingPropertyId(null);
      setExpandingFloorPlanKey(null);
      setOpeningFloorPlanKey(null);
      floorPlanImageRequestRef.current += 1;
      setFloorPlanDialog({
        open: false,
        imageUrl: "",
        modelName: "",
        error: false,
        units: [],
      });
      setShowMap(false);
      setAppliedPropertySearchQuery("");
      const mode = restored.selectionMode === "unit" && !canUseUnitMode
        ? "floorplan" : restored.selectionMode ?? "floorplan";
      setSelectionMode(mode);
      setSelectedUnits(new Map());
      setSelectedFloorPlans(new Map());
      setSelectedClient(null);
      setDraftClientIdToHydrate(null);
      setSendListingsDialogOpen(false);
      setSendDialogDraft(null);
      setListingsToSend([]);
      pendingSendListingsReopen.current = false;
      setPendingUnitRestorations(mode === "unit" ? restored.selectedKeys : null);
      setPendingFloorPlanRestorations(mode === "floorplan" ? restored.selectedKeys : null);
      setQueuedCriteriaSearch(true);
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [
    canUseUnitMode, discardResultSnapshot, draftClientIdToHydrate,
    initialClientId, selectedClient,
  ]);

  // Load initial data (neighborhoods now cached via React Query hook)
  useEffect(() => {
    loadClients();
  }, []);

  // Auto-select client from props
  useEffect(() => {
    if (postClearAwaitingUserRef.current) return;
    if (initialClientId && clientsLoaded && clients.length > 0) {
      const client = clients.find((c) => c.id === initialClientId);
      if (client) {
        setSelectedClient(client);
      }
    }
  }, [initialClientId, clientsLoaded, clients]);

  // Apply initial neighborhoods from navigation (e.g. dashboard saved searches)
  useEffect(() => {
    if (postClearAwaitingUserRef.current) return;
    if (initialNeighborhoods && initialNeighborhoods.length > 0) {
      setSelectedNeighborhoods(initialNeighborhoods);
    }
  }, [initialNeighborhoods]);

  // Load saved searches when client changes
  useEffect(() => {
    if (postClearAwaitingUserRef.current) return;
    if (selectedClient) {
      loadClientSavedSearches(selectedClient.id);
      // Avoid prefill while draft restore is still resolving to prevent
      // mount-time state thrash/flicker.
      if (draftRestoreComplete) {
        // Pre-populate search fields from client preferences
        prefillFromClientPreferences(selectedClient);
      }
    } else {
      setClientSavedSearches([]);
    }
  }, [selectedClient, draftRestoreComplete]);

  useEffect(() => {
    if (postClearAwaitingUserRef.current) return;
    if (!initialSearchCriteria) {
      initialSearchAppliedRef.current = false;
      return;
    }

    if (initialSearchAppliedRef.current) return;
    if (initialClientId && clientsLoaded && !selectedClient) return;

    const c = initialSearchCriteria;
    const minPrice = c.min_price ?? PRICE_MIN;
    const maxPrice = c.max_price ?? PRICE_MAX;

    setPriceRange([minPrice, maxPrice]);
    setSelectedBedrooms(c.selected_bedrooms ?? []);
    setMinSqft(c.min_sqft ?? "");
    setSelectedNeighborhoods(c.neighborhoods ?? []);
    setSelectedAmenities(sanitizeAmenities(c.amenities));
    setSelectedManagement(c.management_companies ?? []);
    setNearAddress(c.near_address ?? "");
    setMaxPricePerSqft(c.price_per_sqft_max ?? "");
    setAvailableOnly(c.available_only ?? true);
    setSortBy(c.sort_by || "price");

    const savedMoveInDate = c.move_in_date
      ? parseMoveInDate(c.move_in_date)
      : null;
    setMoveInDate(savedMoveInDate);

    // Restore selection mode and queue restorations (enforce admin gate)
    const rawMode = c.selection_mode || (c.selected_unit_ids?.length ? "unit" : "floorplan");
    const savedMode = rawMode === "unit" && !canUseUnitMode ? "floorplan" : rawMode;
    setSelectionMode(savedMode);

    if (savedMode === "unit" && c.selected_unit_ids?.length) {
      setPendingUnitRestorations(c.selected_unit_ids);
      setPendingFloorPlanRestorations(null);
    } else if (savedMode === "floorplan" && c.selected_floor_plan_keys?.length) {
      setPendingFloorPlanRestorations(c.selected_floor_plan_keys);
      setPendingUnitRestorations(null);
    } else {
      setPendingUnitRestorations(null);
      setPendingFloorPlanRestorations(null);
      setSelectedUnits(new Map());
      setSelectedFloorPlans(new Map());
    }

    initialSearchAppliedRef.current = true;
    setDraftSaveGate(true); // Initial criteria applied — saves are now safe
    setQueuedCriteriaSearch(true);
  }, [
    clientsLoaded,
    initialClientId,
    initialSearchCriteria,
    selectedClient,
  ]);

  // Group results when search results change
  useEffect(() => {
    if (searchResults.length > 0) {
      groupResults();
    }
  }, [searchResults, moveInDate]);

  // Pre-populate search fields from client preferences
  // Skip if draft was restored (draft values take precedence over client defaults)
  const prefillFromClientPreferences = useCallback((client: Client) => {
    if (draftRestored.current) {
      // Draft was restored with real data — don't overwrite with client defaults.
      // Clear the flag so future client switches DO prefill normally.
      draftRestored.current = false;
      return;
    }
    console.log("Prefilling from client:", client);
    console.log("Client move_in_timeline:", client.move_in_timeline);

    // Set max price from budget
    if (client.max_budget && client.max_budget > 0) {
      setPriceRange([PRICE_MIN, Math.min(client.max_budget, PRICE_MAX)]);
    }

    // Set bedrooms from min_bedrooms preference
    if (client.min_bedrooms !== undefined && client.min_bedrooms !== null) {
      const minBed = client.min_bedrooms;
      // Select this bedroom option
      const bedroomsToSelect: string[] = [];
      if (minBed === 0) bedroomsToSelect.push("0");
      else if (minBed === 1) bedroomsToSelect.push("1");
      else if (minBed === 2) bedroomsToSelect.push("2");
      else if (minBed >= 3) bedroomsToSelect.push("3+");

      setSelectedBedrooms(bedroomsToSelect);
    }

    // Prefer the canonical date; retain the legacy timeline fallback
    const parsedDate = parseMoveInDate(clientMoveIn(client));
    console.log("Parsed date result:", parsedDate);
    setMoveInDate(parsedDate);

    setSnackbar({
      open: true,
      message: `Search pre-filled with ${client.first_name}'s preferences`,
      severity: "success",
    });
  }, []);

  // Neighborhoods now loaded via useSearchNeighborhoods() hook (cached 24h)

  const loadClients = async () => {
    try {
      const { data, error } = await supabase
        .from("clients")
        .select(
          "id, first_name, last_name, email, phone, max_budget, min_bedrooms, move_in_date, move_in_timeline"
        )
        .order("first_name", { ascending: true });
      if (error) {
        console.error("Error loading clients:", error);
        throw error;
      }
      console.log("Loaded clients:", data?.length || 0);
      if (data && data.length > 0) {
        console.log("Sample client with move_in_timeline:", data[0]);
      }
      setClients(data || []);
      setClientsLoaded(true);
    } catch (err) {
      console.error("Error loading clients:", err);
      setClients([]);
      setClientsLoaded(true);
    }
  };

  const loadClientSavedSearches = async (clientId: string) => {
    try {
      const { data, error } = await supabase
        .from("saved_searches")
        .select("*")
        .eq("client_id", clientId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setClientSavedSearches(data || []);
    } catch (err) {
      console.error("Error loading saved searches:", err);
    }
  };

  const loadAllSavedSearches = async () => {
    try {
      const { data, error } = await supabase
        .from("saved_searches")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;

      const searches = data || [];
      // Batch-fetch client names
      const clientIds = [...new Set(searches.map((s) => s.client_id).filter(Boolean))];
      const nameMap = new Map<string, string>();
      if (clientIds.length > 0) {
        const { data: clientData } = await supabase
          .from("clients")
          .select("id,first_name,last_name")
          .in("id", clientIds);
        (clientData || []).forEach((c) => {
          nameMap.set(c.id, `${c.first_name || ""} ${c.last_name || ""}`.trim());
        });
      }

      setAllSavedSearches(
        searches.map((s) => ({
          ...s,
          client_name: nameMap.get(s.client_id) || "Unknown",
        }))
      );
      setAllSearchesLoaded(true);
    } catch (err) {
      console.error("Error loading all saved searches:", err);
    }
  };

  const handleLoadSavedSearchFromDrawer = (search: SavedSearch & { client_name?: string }) => {
    // Select the client first
    const client = clients.find((c) => c.id === search.client_id);
    if (client) setSelectedClient(client);
    // Load filters + run
    handleLoadSavedSearch(search);
    setSavedSearchesOpen(false);
  };

  const displayedSavedSearches = useMemo(() => {
    if (!isDemo) return allSavedSearches;

    const jane =
      clients.find(
        (client) =>
          client.first_name === "Jane" && client.last_name === "Demo"
      ) || clients.find((client) => client.first_name === "Jane");

    return DEMO_SAVED_SEARCHES.map((search, index) => ({
      id: search.id,
      client_id: jane?.id || `demo-client-${index}`,
      client_name: search.client,
      name: search.name,
      search_criteria: search.searchCriteria,
      result_count: 0,
      created_at: new Date(0).toISOString(),
    }));
  }, [allSavedSearches, clients, isDemo]);

  const handleDeleteSavedSearch = async (e: React.MouseEvent, searchId: string) => {
    e.stopPropagation();
    try {
      await fetchWithAuth(`/saved-searches/${searchId}`, { method: "DELETE" });
      setAllSavedSearches((prev) => prev.filter((s) => s.id !== searchId));
      setClientSavedSearches((prev) => prev.filter((s) => s.id !== searchId));
    } catch (err) {
      console.error("Error deleting saved search:", err);
    }
  };

  const groupResults = () => {
    setGroupedResults(
      groupSearchResults(searchResults, moveInDate || new Date())
    );
  };

  const handleSearch = useCallback(async () => {
    if (postClearAwaitingUserRef.current) return;
    const requestEpoch = searchRequestEpochRef.current + 1;
    searchRequestEpochRef.current = requestEpoch;
    const requestIsCurrent = () =>
      searchRequestEpochRef.current === requestEpoch;
    const executedCriteria: SearchCriteriaSnapshot = {
      nearAddress: nearAddress.slice(0, 500),
      filters: {
        priceRange,
        selectedBedrooms,
        minSqft,
        availableOnly,
        moveInDate: moveInDate?.toISOString().split("T")[0] ?? null,
        sortBy,
        selectedNeighborhoods,
        selectedAmenities,
        selectedManagement,
        maxPricePerSqft,
        noFloorPlanImage,
      },
    };
    const resultClientId =
      selectedClient?.id ?? draftClientIdToHydrate ?? initialClientId ?? null;
    const executedFingerprint = searchCriteriaFingerprint(executedCriteria);
    setLoading(true);
    setError(null);
    // Clear selections on new search (saved search restore will re-populate via pending state)
    setSelectedUnits(new Map());
    setSelectedFloorPlans(new Map());
    if (!pendingFloorPlanRestorationsRef.current && !pendingUnitRestorationsRef.current) {
      setSelectionMode("floorplan");
    }

    try {
      const params = new URLSearchParams();

      // Price range
      if (priceRange[0] > PRICE_MIN) {
        params.set("min_price", String(priceRange[0]));
      }
      if (priceRange[1] < PRICE_MAX) {
        params.set("max_price", String(priceRange[1]));
      }

      // Bedrooms - convert selection to min/max beds for API
      if (selectedBedrooms.length > 0) {
        const bedValues = selectedBedrooms.map((b) =>
          b === "3+" ? 3 : parseInt(b)
        );
        const minBed = Math.min(...bedValues);
        const maxBed = selectedBedrooms.includes("3+")
          ? 10
          : Math.max(...bedValues);
        params.set("min_beds", String(minBed));
        params.set("max_beds", String(maxBed));
      }

      // Square footage
      if (minSqft !== "") params.set("min_sqft", String(minSqft));

      // "Close to X" proximity — when a near-address is set, hit the geo endpoint
      // (PKT-0125) instead of the normal search. It returns the same unit-level
      // result shape (plus distance), ranked nearest-first, so the existing
      // results render unchanged. price/beds/sqft/available apply; neighborhood/
      // amenity/sort don't (proximity is the ordering).
      const trimmedNear = nearAddress.trim();
      if (trimmedNear) {
        params.set("near", trimmedNear);
        params.set("available_only", String(availableOnly));
        // The backend caps unit ROWS; a property with many units (e.g. 95) can
        // crowd out the rest, so pull a generous page to surface the nearest
        // ~30-50 properties (a proper per-property cap is a backend follow-up).
        params.set("limit", "500");
        const geo = await fetchWithAuth<{
          results: SearchResult[];
          message?: string | null;
          anchor?: { resolved?: boolean; matched_address?: string | null };
        }>(`/search/geo?${params}`);
        if (!requestIsCurrent()) return;
        let geoData = geo.results || [];
        if (availableOnly) {
          const targetDate = moveInDate || new Date();
          geoData = geoData.filter((item: any) =>
            isAvailableByDate(item.status, item.availability_date, targetDate)
          );
        }
        executedResultFingerprintRef.current = executedFingerprint;
        setSearchResults(geoData);
        if (!searchEntryNeedsBindingRef.current) {
          saveResultSnapshot(resultClientId, executedCriteria, geoData);
        }
        setHasExecutedSearch(true);
        setGeoNotice(
          geo.message
            ? geo.message
            : geo.anchor?.matched_address
              ? `Showing properties nearest to ${geo.anchor.matched_address}.`
              : null
        );
        activityService.trackSearchExecuted(
          {
            priceRange,
            selectedBedrooms,
            minSqft,
            availableOnly,
            sortBy,
            selectedNeighborhoods,
            selectedAmenities,
            maxPricePerSqft,
          },
          geoData.length
        );
        return;
      }
      setGeoNotice(null);

      // Advanced filters
      if (maxPricePerSqft !== "") {
        params.set("price_per_sqft_max", String(maxPricePerSqft));
      }

      selectedNeighborhoods.forEach((n) => params.append("neighborhoods", n));
      selectedAmenities.forEach((a) => params.append("amenities", a));
      params.set("sort_by", sortBy);

      const hasAdvancedFilters =
        selectedNeighborhoods.length > 0 ||
        selectedAmenities.length > 0 ||
        maxPricePerSqft !== "";

      const endpoint = hasAdvancedFilters
        ? "search/advanced-filters"
        : "search/comprehensive";

      if (!hasAdvancedFilters) {
        params.set("available_only", String(availableOnly));
      }

      let data = await fetchWithAuth<SearchResult[]>(`/${endpoint}?${params}`);
      if (!requestIsCurrent()) return;

      console.log("Search result sample:", data[0]);

      // When availableOnly is true, filter to only include units that are available
      // by the target move-in date (or today if no date specified)
      if (availableOnly) {
        const targetDate = moveInDate || new Date();
        data = data.filter((item: any) =>
          isAvailableByDate(item.status, item.availability_date, targetDate)
        );
      }

      executedResultFingerprintRef.current = executedFingerprint;
      setSearchResults(data);
      if (!searchEntryNeedsBindingRef.current) {
        saveResultSnapshot(resultClientId, executedCriteria, data);
      }
      setHasExecutedSearch(true);
      activityService.trackSearchExecuted(
        {
          priceRange,
          selectedBedrooms,
          minSqft,
          availableOnly,
          sortBy,
          selectedNeighborhoods,
          selectedAmenities,
          maxPricePerSqft,
        },
        data.length
      );
    } catch (err: any) {
      if (!requestIsCurrent()) return;
      console.error("Search error:", err);
      setError(err.message || "Failed to fetch results");
    } finally {
      if (requestIsCurrent()) setLoading(false);
    }
  }, [
    availableOnly,
    maxPricePerSqft,
    minSqft,
    moveInDate,
    nearAddress,
    priceRange,
    selectedAmenities,
    selectedBedrooms,
    selectedClient,
    selectedManagement,
    selectedNeighborhoods,
    noFloorPlanImage,
    draftClientIdToHydrate,
    initialClientId,
    saveResultSnapshot,
    sortBy,
  ]);

  useEffect(() => {
    if (!queuedCriteriaSearch) return;
    setQueuedCriteriaSearch(false);
    handleSearch();
  }, [queuedCriteriaSearch, handleSearch]);

  const handleToggleProperty = (propertyId: string) => {
    if (expandingPropertyId === propertyId) return;
    if (expandedProperties.has(propertyId)) {
      setExpandedProperties((prev) => {
        const next = new Set(prev);
        next.delete(propertyId);
        return next;
      });
      return;
    }

    flushSync(() => setExpandingPropertyId(propertyId));
    window.setTimeout(() => {
      startTransition(() => {
        setExpandedProperties((prev) => {
          const next = new Set(prev);
          next.add(propertyId);
          return next;
        });
      });
    }, 0);
  };

  const handlePropertyExpandEntered = (propertyId: string) => {
    setExpandingPropertyId((current) =>
      current === propertyId ? null : current
    );
    // Mark the expanded section as ready for the walkthrough tour
    // Uses requestAnimationFrame to ensure layout is stable after Collapse finishes
    requestAnimationFrame(() => {
      // Remove any previous ready markers
      document.querySelectorAll('[data-tour="floor-plan-section-ready"]').forEach(
        (el) => el.removeAttribute("data-tour")
      );
      // Mark this property's expanded section
      const card = document.querySelector(`[data-property-id="${propertyId}"]`);
      if (card) {
        const collapseContent = card.querySelector('[class*="MuiCollapse-wrapperInner"]');
        if (collapseContent) {
          (collapseContent as HTMLElement).setAttribute("data-tour", "floor-plan-section-ready");
        }
      }
    });
  };

  const handleCloseFloorPlanDialog = () => {
    floorPlanImageRequestRef.current += 1;
    setOpeningFloorPlanKey(null);
    setFloorPlanDialog({
      open: false,
      imageUrl: "",
      modelName: "",
      error: false,
      units: [],
    });
  };

  const handleToggleFloorPlan = (fpKey: string) => {
    if (expandingFloorPlanKey === fpKey) return;
    if (expandedFloorPlans.has(fpKey)) {
      setExpandedFloorPlans((prev) => {
        const next = new Set(prev);
        next.delete(fpKey);
        return next;
      });
      return;
    }

    flushSync(() => setExpandingFloorPlanKey(fpKey));
    window.setTimeout(() => {
      startTransition(() => {
        setExpandedFloorPlans((prev) => {
          const next = new Set(prev);
          next.add(fpKey);
          return next;
        });
      });
    }, 0);
  };

  const handleFloorPlanExpandEntered = (fpKey: string) => {
    setExpandingFloorPlanKey((current) =>
      current === fpKey ? null : current
    );
  };

  const handleToggleUnit = (
    unitKey: string,
    unit: GroupedUnit,
    floorPlan: GroupedFloorPlan,
    property: SearchGroupedProperty
  ) => {
    setSelectedUnits((prev) => {
      const next = new Map(prev);
      if (next.has(unitKey)) next.delete(unitKey);
      else next.set(unitKey, { unit, floorPlan, property });
      return next;
    });
  };

  const handleToggleFloorPlanSelection = (
    floorPlan: GroupedFloorPlan,
    property: SearchGroupedProperty
  ) => {
    // Safety guard: don't allow selecting unavailable floor plans
    if (floorPlan.available_count === 0) return;

    const fpKey = buildFloorPlanKey(
      property.property_id,
      floorPlan.model_name,
      floorPlan.bedrooms,
      floorPlan.bathrooms,
      floorPlan.square_feet
    );
    setSelectedFloorPlans((prev) => {
      const next = new Map(prev);
      if (next.has(fpKey)) next.delete(fpKey);
      else next.set(fpKey, { floorPlan, property });
      return next;
    });
  };

  const handleSwitchSelectionMode = (mode: "floorplan" | "unit") => {
    setSelectionMode(mode);
    if (mode === "floorplan") {
      setSelectedUnits(new Map());
    } else {
      setSelectedFloorPlans(new Map());
    }
  };

  const handleClearFilters = () => {
    setPriceRange([PRICE_MIN, PRICE_MAX]);
    setSelectedBedrooms([]);
    setMinSqft("");
    setMoveInDate(null);
    setSelectedNeighborhoods([]);
    setSelectedAmenities([]);
    setSelectedManagement([]);
    setMaxPricePerSqft("");
    setAvailableOnly(true);
    setNoFloorPlanImage(false);
    setSortBy("price");
    setNearAddress("");
    setGeoNotice(null);
    setHasExecutedSearch(false);
    // Back to the start: drop the results, list, map view and selections.
    // The selected client is intentionally left alone.
    setSearchResults([]);
    setGroupedResults([]);
    setShowMap(false);
    setSelectedUnits(new Map());
    setSelectedFloorPlans(new Map());
    setSelectionMode("floorplan");
    setPendingUnitRestorations(null);
    setPendingFloorPlanRestorations(null);
    pendingUnitRestorationsRef.current = null;
    pendingFloorPlanRestorationsRef.current = null;
    // Clear draft and suppress the next save so defaults don't recreate it
    draftSuppressNextSave.current = true;
    clearDraft(selectedClient?.id ?? draftClientIdToHydrate ?? initialClientId ?? null);
  };

  // Full reset — mobile "Clear" button. Drops the client, empties the
  // results grid, clears selections, and resets filters. Gets the page
  // back to the initial "pick a client and search" state in one tap.
  const handleClearAll = () => {
    searchRequestEpochRef.current += 1;
    executedResultFingerprintRef.current = null;
    setQueuedCriteriaSearch(false);
    setLoading(false);
    setError(null);
    handleClearFilters();
    setGroupedResults([]);
    setSelectedClient(null);
    setAppliedPropertySearchQuery("");
    setSelectedFloorPlans(new Map());
    setSelectedUnits(new Map());
    setListingsToSend([]);
    setExpandedProperties(new Set());
    setExpandedFloorPlans(new Set());
    setExpandingPropertyId(null);
    setExpandingFloorPlanKey(null);
    handleCloseFloorPlanDialog();
    setSendListingsDialogOpen(false);
    setSendDialogDraft(null);
    pendingSendListingsReopen.current = false;
  };

  // A same-user Clear Local Data event can originate in this tab or a sibling
  // tab. Reset the mounted Search mirror before allowing the new persistence
  // generation to accept user edits.
  const handledClearRevisionRef = useRef(0);
  useEffect(() => {
    if (
      clearRevision === 0 ||
      handledClearRevisionRef.current === clearRevision
    ) return;
    handledClearRevisionRef.current = clearRevision;
    bootstrapEpochRef.current += 1;
    postClearAwaitingUserRef.current = true;
    setDraftSaveGate(false);
    handleClearAll();
    acknowledgeLocalClear();
  }, [clearRevision, acknowledgeLocalClear, handleClearAll]);

  const handleLoadSavedSearch = (search: SavedSearch) => {
    const c = search.search_criteria;

    // Handle price range
    const minPrice = c.min_price ?? PRICE_MIN;
    const maxPrice = c.max_price ?? PRICE_MAX;
    setPriceRange([minPrice, maxPrice]);

    // Handle bedrooms
    setSelectedBedrooms(c.selected_bedrooms ?? []);

    setMinSqft(c.min_sqft ?? "");
    setSelectedNeighborhoods(c.neighborhoods ?? []);
    setSelectedAmenities(sanitizeAmenities(c.amenities));
    setSelectedManagement(c.management_companies ?? []);
    setNearAddress(c.near_address ?? "");
    setMaxPricePerSqft(c.price_per_sqft_max ?? "");
    setAvailableOnly(c.available_only ?? true);
    setSortBy(c.sort_by || "price");

    // Handle move-in date
    const savedMoveInDate = c.move_in_date
      ? parseMoveInDate(c.move_in_date)
      : null;
    setMoveInDate(savedMoveInDate);

    // Restore selection mode and queue restorations (enforce admin gate)
    const rawMode = c.selection_mode || (c.selected_unit_ids?.length ? "unit" : "floorplan");
    const savedMode = rawMode === "unit" && !canUseUnitMode ? "floorplan" : rawMode;
    setSelectionMode(savedMode);

    if (savedMode === "unit" && c.selected_unit_ids?.length) {
      setPendingUnitRestorations(c.selected_unit_ids);
      setPendingFloorPlanRestorations(null);
    } else if (savedMode === "floorplan" && c.selected_floor_plan_keys?.length) {
      setPendingFloorPlanRestorations(c.selected_floor_plan_keys);
      setPendingUnitRestorations(null);
    } else {
      setPendingUnitRestorations(null);
      setPendingFloorPlanRestorations(null);
      setSelectedUnits(new Map());
      setSelectedFloorPlans(new Map());
    }

    setQueuedCriteriaSearch(true);
  };

  const handleViewFloorPlan = (
    imageUrl: string,
    modelName: string,
    floorPlanKey: string,
    units: GroupedUnit[] = []
  ) => {
    flushSync(() => setOpeningFloorPlanKey(floorPlanKey));
    const requestId = floorPlanImageRequestRef.current + 1;
    floorPlanImageRequestRef.current = requestId;

    window.setTimeout(() => {
      const preloader = new Image();
      preloader.onload = () => {
        if (floorPlanImageRequestRef.current !== requestId) return;
        setOpeningFloorPlanKey(null);
        setFloorPlanDialog({
          open: true,
          imageUrl,
          modelName,
          error: false,
          units,
        });
      };
      preloader.onerror = () => {
        if (floorPlanImageRequestRef.current !== requestId) return;
        setOpeningFloorPlanKey(null);
        setFloorPlanDialog({
          open: true,
          imageUrl: "",
          modelName,
          error: true,
          units,
        });
      };
      preloader.src = imageUrl;
    }, 0);
  };

  const advancedFilterCount =
    selectedNeighborhoods.length +
    selectedAmenities.length +
    selectedManagement.length +
    (maxPricePerSqft !== "" ? 1 : 0) +
    (noFloorPlanImage ? 1 : 0) +
    (nearAddress.trim() ? 1 : 0);

  const availableManagement = Array.from(new Set(
    groupedResults.map((p) => p.property_management).filter((m): m is string => !!m)
  )).sort();


  const handleSaveSearch = async () => {
    if (!selectedClient || !searchName.trim()) {
      setSnackbar({
        open: true,
        message: "Please select a client and enter a search name",
        severity: "error",
      });
      return;
    }
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        setSnackbar({
          open: true,
          message: "You must be logged in to save searches",
          severity: "error",
        });
        return;
      }

      const searchCriteria: SearchCriteria = {
        min_price: priceRange[0] > PRICE_MIN ? priceRange[0] : null,
        max_price: priceRange[1] < PRICE_MAX ? priceRange[1] : null,
        selected_bedrooms: selectedBedrooms,
        min_sqft: minSqft || null,
        available_only: availableOnly,
        amenities: selectedAmenities,
        neighborhoods: selectedNeighborhoods,
        management_companies: selectedManagement,
        price_per_sqft_max: maxPricePerSqft || null,
        sort_by: sortBy,
        move_in_date: moveInDate ? formatDateForInput(moveInDate) : null,
        near_address: nearAddress.trim() || undefined,
        search_mode: nearAddress.trim() ? "proximity_search" : "manual_search",
        selection_mode: selectionMode,
        selected_unit_ids:
          selectionMode === "unit" && selectedUnits.size > 0
            ? [...selectedUnits.keys()]
            : undefined,
        selected_floor_plan_keys:
          selectionMode === "floorplan" && selectedFloorPlans.size > 0
            ? [...selectedFloorPlans.keys()]
            : undefined,
      };

      const reproducibilityEnabled =
        process.env.REACT_APP_SEARCH_REPRODUCIBILITY_ENABLED === "true";
      let reproducibilityFields: Record<string, unknown> = {};
      if (reproducibilityEnabled) {
        const manifest = await fetchWithAuth<Record<string, any>>(
          "/search-reproducibility/manifest",
          {
            method: "POST",
            body: JSON.stringify({
              criteria: searchCriteria,
              ordered_results: searchResults,
              mode: searchCriteria.search_mode,
            }),
          }
        );
        reproducibilityFields = {
          search_schema_version: manifest.schema_version,
          criteria_fingerprint: manifest.input_fingerprint,
          algorithm_key: manifest.algorithm_key,
          algorithm_version: manifest.algorithm_version,
          inventory_contract_version: manifest.inventory_contract_version,
          ordered_result_snapshot: manifest.ordered_results,
          output_signature: manifest.output_signature,
        };
      }

      const { data: saved, error } = await supabase
        .from("saved_searches")
        .insert({
          user_id: user.id,
          client_id: selectedClient.id,
          name: searchName,
          search_criteria: searchCriteria,
          result_count: searchResults.length,
          ...reproducibilityFields,
        })
        .select()
        .single();

      if (error) {
        console.error("Save failed:", error);
        setSnackbar({
          open: true,
          message: `Failed to save search: ${error.message}`,
          severity: "error",
        });
        return;
      }

      activityService.trackSearchSaved(saved.id, searchName, selectedClient.id);
      setShowSaveDialog(false);
      setSearchName("");
      loadClientSavedSearches(selectedClient.id);
      setSnackbar({
        open: true,
        message: "Search saved successfully!",
        severity: "success",
      });
    } catch (err) {
      console.error("Error saving search:", err);
      setSnackbar({
        open: true,
        message: "Error saving search",
        severity: "error",
      });
    }
  };

  // Fetch freshness data when search results change
  useEffect(() => {
    const propertyIds = groupedResults.map((p) => p.property_id);
    if (propertyIds.length === 0) return;
    checkPropertyFreshness(propertyIds)
      .then((result) => {
        const map = new Map<string, PropertyFreshnessItem>();
        result.properties.forEach((p) => map.set(p.property_id, p));
        setFreshnessData(map);
      })
      .catch(() => {});
  }, [groupedResults]);

  // Restore saved unit selections after search results arrive
  useEffect(() => {
    if (!pendingUnitRestorations || groupedResults.length === 0) return;

    const savedIds = new Set(pendingUnitRestorations);
    const restored = new Map<
      string,
      { unit: GroupedUnit; floorPlan: GroupedFloorPlan; property: SearchGroupedProperty }
    >();

    for (const property of groupedResults) {
      for (const fp of property.floor_plans) {
        for (const unit of fp.units) {
          if (savedIds.has(unit.unique_id)) {
            restored.set(unit.unique_id, { unit, floorPlan: fp, property });
          }
        }
      }
    }

    setSelectedUnits(restored);
    setPendingUnitRestorations(null);

    const restoredCount = restored.size;
    const savedCount = savedIds.size;

    if (savedCount > 0) {
      if (restoredCount === savedCount) {
        setSnackbar({
          open: true,
          message: `Restored ${restoredCount} previously selected unit${restoredCount !== 1 ? "s" : ""}`,
          severity: "success",
        });
      } else if (restoredCount > 0) {
        setSnackbar({
          open: true,
          message: `Restored ${restoredCount} of ${savedCount} units — ${savedCount - restoredCount} no longer available`,
          severity: "error",
        });
      } else {
        setSnackbar({
          open: true,
          message: `None of the ${savedCount} previously selected units are available`,
          severity: "error",
        });
      }
    }
  }, [groupedResults, pendingUnitRestorations]);

  // Restore saved floor plan selections after search results arrive
  useEffect(() => {
    if (!pendingFloorPlanRestorations || groupedResults.length === 0) return;

    const savedKeys = new Set(pendingFloorPlanRestorations);
    const restored = new Map<
      string,
      { floorPlan: GroupedFloorPlan; property: SearchGroupedProperty }
    >();

    for (const property of groupedResults) {
      for (const fp of property.floor_plans) {
        const fpKey = buildFloorPlanKey(
          property.property_id,
          fp.model_name,
          fp.bedrooms,
          fp.bathrooms,
          fp.square_feet
        );
        if (savedKeys.has(fpKey)) {
          restored.set(fpKey, { floorPlan: fp, property });
        }
      }
    }

    setSelectedFloorPlans(restored);
    setPendingFloorPlanRestorations(null);

    const restoredCount = restored.size;
    const savedCount = savedKeys.size;

    if (savedCount > 0) {
      if (restoredCount === savedCount) {
        setSnackbar({
          open: true,
          message: `Restored ${restoredCount} previously selected floor plan${restoredCount !== 1 ? "s" : ""}`,
          severity: "success",
        });
      } else if (restoredCount > 0) {
        setSnackbar({
          open: true,
          message: `Restored ${restoredCount} of ${savedCount} floor plans — ${savedCount - restoredCount} no longer available`,
          severity: "error",
        });
      }
    }
  }, [groupedResults, pendingFloorPlanRestorations]);

  const formatFreshnessDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return "Never";
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    if (diffHours < 1) return "Just now";
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  const openSendEmail = async (options?: { preserveDraft?: boolean }) => {
    const hasSelection = selectionMode === "floorplan"
      ? selectedFloorPlans.size > 0
      : selectedUnits.size > 0;

    if (!selectedClient || !hasSelection) {
      setSnackbar({
        open: true,
        message: `Please select a client and at least one ${selectionMode === "floorplan" ? "floor plan" : "unit"}`,
        severity: "error",
      });
      return;
    }

    const listings = buildFreshListingSelections(
      selectionMode, selectedFloorPlans, selectedUnits, moveInDate || undefined
    );

    // Fresh explicit opens should use the latest saved template/defaults
    // instead of reviving the last composed draft. Persisted compose text
    // is only preserved for tab-discard recovery, which passes preserveDraft.
    if (!options?.preserveDraft) {
      setSendDialogDraft(null);
    }
    setListingsToSend(listings);
    setSendListingsDialogOpen(true);
  };

  const handleSendEmail = () => {
    void openSendEmail();
  };

  // Re-open the Send-Listings dialog if it was open when the tab was left.
  // Gated on selections having rehydrated so `listingsToSend` gets rebuilt
  // from the same set the user had selected before. One-shot.
  useEffect(() => {
    if (!pendingSendListingsReopen.current) return;
    if (selectedFloorPlans.size === 0 && selectedUnits.size === 0) return;
    pendingSendListingsReopen.current = false;
    void openSendEmail({ preserveDraft: true });
  }, [selectedFloorPlans, selectedUnits]);

  const handleCloseSendListingsDialog = () => {
    setSendListingsDialogOpen(false);
    setSendDialogDraft(null);
  };

  const handleRemoveListingFromSend = (listingId: string) => {
    setListingsToSend((prev) => prev.filter((l) => l.id !== listingId));

    if (selectionMode === "floorplan") {
      const next = new Map(selectedFloorPlans);
      next.delete(listingId);
      setSelectedFloorPlans(next);
    } else {
      const newSelectedUnits = new Map(selectedUnits);
      const entries = Array.from(selectedUnits.entries());
      for (let i = 0; i < entries.length; i++) {
        const [key, value] = entries[i];
        if (value.unit.unique_id === listingId) {
          newSelectedUnits.delete(key);
          break;
        }
      }
      setSelectedUnits(newSelectedUnits);
    }
  };

  const handleSendListingsSuccess = () => {
    setSnackbar({
      open: true,
      message: `Listings sent to ${selectedClient?.email}!`,
      severity: "success",
    });
    setSelectedUnits(new Map());
    setSelectedFloorPlans(new Map());
    setListingsToSend([]);
    // Clear persisted compose text so a brand-new email for this client
    // starts with fresh defaults rather than the just-sent message.
    setSendDialogDraft(null);
  };

  // Apply post-search property management filter (used for both counts and rendering)
  const filteredGroupedResults = useMemo(() => {
    const normalizedQuery = appliedPropertySearchQuery.trim().toLowerCase();

    return groupedResults.filter((property) => {
      const matchesManagement =
        selectedManagement.length === 0 ||
        selectedManagement.includes(property.property_management || "");

      if (!matchesManagement) return false;

      if (noFloorPlanImage && !property.floor_plans.some((fp) => !fp.image_url)) {
        return false;
      }

      if (!normalizedQuery) return true;

      const haystack = [
        property.property_name,
        property.neighborhood,
        property.property_management,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(normalizedQuery);
    });
  }, [groupedResults, selectedManagement, appliedPropertySearchQuery, noFloorPlanImage]);

  // Build the set of property IDs that pass the filter so unit counts can match
  const filteredPropertyIds = useMemo(
    () => new Set(filteredGroupedResults.map((p) => p.property_id)),
    [filteredGroupedResults]
  );

  // One pin per filtered property, using coords carried by geo-search results.
  // Normal searches don't include coords → no pins → the Map toggle hides itself.
  const mapPoints: MapPoint[] = useMemo(() => {
    const seen = new Set<string>();
    const pts: MapPoint[] = [];
    for (const r of searchResults) {
      if (seen.has(r.property_id) || !filteredPropertyIds.has(r.property_id)) continue;
      if (typeof r.latitude !== "number" || typeof r.longitude !== "number") continue;
      seen.add(r.property_id);
      pts.push({
        id: r.property_id,
        name: r.property_name,
        lat: r.latitude,
        lng: r.longitude,
        price: r.current_price,
        distanceMiles: r.distance_miles ?? null,
      });
    }
    return pts;
  }, [searchResults, filteredPropertyIds]);

  const filteredSearchResults = useMemo(
    () => searchResults.filter((r) => filteredPropertyIds.has(r.property_id)),
    [searchResults, filteredPropertyIds]
  );

  const totalUnits = filteredSearchResults.length;
  const targetDateForCount = moveInDate || new Date();
  const availableUnitsCount = filteredSearchResults.filter((r) =>
    isAvailableByDate(r.status, r.availability_date, targetDateForCount)
  ).length;

  // Price slider value text
  const priceValueText = (value: number) => formatPriceShort(value);

  return (
    <Box
      sx={{ pb: 8 }}
      onClickCapture={resumePersistenceAfterUserAction}
      onChangeCapture={resumePersistenceAfterUserAction}
    >
      <PropertiesSectionHeader
        context={[
          hasExecutedSearch && !loading && !error ? `${filteredGroupedResults.length} match now` : null,
          selectedClient ? `for ${selectedClient.first_name} ${selectedClient.last_name}` : null,
        ].filter(Boolean).join(' ')}
        actions={<Button variant="outlined" size="small" onClick={() => {
          if (!isDemo) loadAllSavedSearches();
          else setAllSearchesLoaded(true);
          setSavedSearchesOpen(true);
        }}>Saved Searches</Button>}
      />

      {/* Loading bar moved below filters, above search results
          (Grant directive 2026-04-25 — "lower one, above results"). */}

      {/* Search Filters */}
      <Paper
        data-tour="search-filters"
        elevation={0}
        sx={{
          p: { xs: 1.5, sm: 2.5 },
          mb: 2,
          borderRadius: 3,
          backgroundColor: alpha(theme.palette.background.default, 0.4),
        }}
      >
        {/* Client selector */}
        <Autocomplete
          fullWidth
          options={clients}
          getOptionLabel={(option) =>
            `${option.first_name} ${option.last_name}`
          }
          value={selectedClient}
          // Compare by id so the sync-hydrated stub (built from sessionStorage
          // before `clients` loads) matches the real Client object once the
          // clients list arrives — avoids the "value not in options" warning
          // and prevents a visual reset during the swap.
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onChange={(_, newValue) => setSelectedClient(newValue)}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    fontSize: 13,
                    bgcolor: alpha(theme.palette.primary.main, 0.15),
                    color: theme.palette.primary.main,
                  }}
                >
                  {option.first_name?.[0] || ""}
                  {option.last_name?.[0] || ""}
                </Avatar>
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    {option.first_name} {option.last_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.email}
                    {option.max_budget
                      ? ` • Budget: ${formatPrice(option.max_budget)}`
                      : ""}
                  </Typography>
                </Box>
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              placeholder="Select a client..."
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: theme.palette.background.paper,
                  borderRadius: 2,
                },
              }}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <InputAdornment position="start">
                      <PersonIcon color="action" fontSize="small" />
                    </InputAdornment>
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        <Divider sx={{ my: 2 }} />
        {/* Filters — single compact row */}
        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            alignItems: "flex-end",
            flexWrap: { xs: "wrap", md: "nowrap" },
          }}
        >
          {/* Price Range */}
          <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 35%" }, minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: "block" }}>
              Price Range
            </Typography>
            <Box sx={{ px: 1 }}>
              <Slider
                value={priceRange}
                onChange={(_, newValue) =>
                  setPriceRange(newValue as [number, number])
                }
                valueLabelDisplay="auto"
                valueLabelFormat={priceValueText}
                min={PRICE_MIN}
                max={PRICE_MAX}
                step={PRICE_STEP}
                sx={{
                  "& .MuiSlider-thumb": { width: 18, height: 18 },
                  "& .MuiSlider-track": { height: 5 },
                  "& .MuiSlider-rail": { height: 5, opacity: 0.3 },
                }}
              />
              <Box sx={{ display: "flex", justifyContent: "space-between", mt: -0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  {formatPriceShort(priceRange[0])}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {priceRange[1] >= PRICE_MAX
                    ? `${formatPriceShort(PRICE_MAX)}+`
                    : formatPriceShort(priceRange[1])}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Bedrooms */}
          <Box sx={{ flex: { xs: "1 1 45%", md: "0 0 150px" } }}>
            <FormControl fullWidth size="small">
              <Select
                multiple
                value={selectedBedrooms}
                onChange={(e) =>
                  setSelectedBedrooms(
                    typeof e.target.value === "string"
                      ? e.target.value.split(",")
                      : e.target.value
                  )
                }
                input={<OutlinedInput />}
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return (
                      <Typography variant="body2" color="text.secondary">
                        Bed/Ba
                      </Typography>
                    );
                  }
                  return selected
                    .map((v) => bedroomOptions.find((o) => o.value === v)?.shortLabel || v)
                    .join(", ");
                }}
                displayEmpty
                sx={{ borderRadius: 2 }}
              >
                {bedroomOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Checkbox
                      checked={selectedBedrooms.indexOf(option.value) > -1}
                      size="small"
                    />
                    <ListItemText primary={option.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Move-in Date */}
          <Box sx={{ flex: { xs: "1 1 45%", md: "0 0 160px" } }}>
            <MoveInDateInput
              value={moveInDate}
              onChange={setMoveInDate}
            />
          </Box>

          {/* Sort By (hidden on mobile — in More Filters drawer) */}
          <Box sx={{ flex: { md: "0 0 160px" }, display: { xs: "none", md: "block" } }}>
            <FormControl fullWidth size="small">
              <Select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                sx={{ borderRadius: 2 }}
              >
                {sortOptions.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* More Filters icon (desktop only) */}
          <Box sx={{ flex: "0 0 auto", display: { xs: "none", sm: "flex" } }}>
            <Badge badgeContent={advancedFilterCount} color="primary">
              <Tooltip title="More Filters">
                <IconButton
                  onClick={() => setShowAdvancedFilters(true)}
                        aria-label="More Filters" title="More Filters"
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <TuneIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Badge>
          </Box>

          {/* Clear (desktop only) */}
          <Box sx={{ flex: "0 0 auto", display: { xs: "none", sm: "block" } }}>
            <Button
              size="small"
              variant="text"
              onClick={handleClearFilters}
              sx={{ textTransform: "none", fontWeight: 500, height: 40 }}
            >
              Clear
            </Button>
          </Box>
        </Box>

        {/* Proximity — always visible, not only in the empty state (PKT-0844). */}
        <Box sx={{ mt: 1.5, mb: 2 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 0.5, fontWeight: 600 }}
          >
            Near a place (optional)
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={nearAddress}
            onChange={(e) => setNearAddress(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSearch();
              }
            }}
            placeholder="Street address to rank results by distance — e.g. 1280 W Peachtree St NW"
            inputProps={{ "aria-label": "Search near a street address" }}
          />
          {geoNotice && (
            <Typography variant="body2" sx={{ color: "info.main", mt: 0.75 }}>
              {geoNotice}
            </Typography>
          )}
        </Box>
        {/* Active Advanced Filters Display */}
        {advancedFilterCount > 0 && (
          <Box
            sx={{
              mt: 2.5,
              pt: 2.5,
              borderTop: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              useFlexGap
              alignItems="center"
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mr: 1, fontWeight: 500 }}
              >
                Active filters:
              </Typography>
              {selectedNeighborhoods.map((n) => (
                <Chip
                  key={n}
                  label={n}
                  size="small"
                  icon={<LocationIcon />}
                  onDelete={() =>
                    setSelectedNeighborhoods((prev) =>
                      prev.filter((x) => x !== n)
                    )
                  }
                  sx={{ mb: 0.5, borderRadius: 1.5 }}
                />
              ))}
              {nearAddress.trim() && (
                <Chip
                  label={`Near: ${nearAddress.trim()}`}
                  size="small"
                  icon={<LocationIcon />}
                  onDelete={() => {
                    setNearAddress("");
                    setGeoNotice(null);
                  }}
                  sx={{ mb: 0.5, borderRadius: 1.5 }}
                />
              )}
              {selectedAmenities.map((a) => {
                const amenity = amenityOptions.find((opt) => opt.value === a);
                return (
                  <Chip
                    key={a}
                    label={amenity?.label || a}
                    size="small"
                    icon={amenity?.icon}
                    onDelete={() =>
                      setSelectedAmenities((prev) =>
                        prev.filter((x) => x !== a)
                      )
                    }
                    sx={{ mb: 0.5, borderRadius: 1.5 }}
                  />
                );
              })}
              {selectedManagement.map((m) => (
                <Chip
                  key={m}
                  label={m}
                  size="small"
                  onDelete={() =>
                    setSelectedManagement((prev) =>
                      prev.filter((x) => x !== m)
                    )
                  }
                  sx={{ mb: 0.5, borderRadius: 1.5 }}
                />
              ))}
              {maxPricePerSqft !== "" && (
                <Chip
                  label={`Max $${maxPricePerSqft}/sqft`}
                  size="small"
                  onDelete={() => setMaxPricePerSqft("")}
                  sx={{ mb: 0.5, borderRadius: 1.5 }}
                />
              )}
            </Stack>
          </Box>
        )}

      </Paper>

      {/* Loading bar — placed above results (Grant 2026-04-25 — "lower one") */}
      <LoadingTopBar loading={loading} />

      {/* Loading State — shimmer skeleton rows matching PropertyRow layout */}
      {loading && (
        <Box>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 1.5, px: 0.5 }}
          >
            Searching properties…
          </Typography>
          <Stack spacing={1.25}>
            {[1, 2, 3, 4, 5].map((i) => (
              <Card
                key={i}
                variant="outlined"
                sx={{ overflow: "hidden", borderRadius: 2 }}
              >
                <Box
                  sx={{
                    display: "flex",
                    height: { xs: 88, sm: 96 },
                  }}
                >
                  <Skeleton
                    animation="wave"
                    variant="rectangular"
                    sx={{
                      width: { xs: 80, sm: 140, md: 170 },
                      height: "100%",
                      flexShrink: 0,
                    }}
                  />
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      p: { xs: 1, sm: 1.5 },
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Skeleton
                        animation="wave"
                        variant="text"
                        width={`${55 + ((i * 13) % 30)}%`}
                        height={22}
                      />
                      <Skeleton
                        animation="wave"
                        variant="text"
                        width={`${40 + ((i * 17) % 25)}%`}
                        height={16}
                      />
                    </Box>
                    <Skeleton
                      animation="wave"
                      variant="circular"
                      width={24}
                      height={24}
                      sx={{ flexShrink: 0 }}
                    />
                  </Box>
                </Box>
              </Card>
            ))}
          </Stack>
        </Box>
      )}

      {/* Results Header */}
      {!loading && groupedResults.length > 0 && (
        <Paper
          data-tour="search-results"
          elevation={0}
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 3,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexWrap: "wrap",
            }}
          >
            <Typography variant="h6" fontWeight={700}>
              {filteredGroupedResults.length} Properties
            </Typography>
            {mapPoints.length > 0 && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => setShowMap((v) => !v)}
                sx={{ textTransform: "none", borderRadius: 1.5 }}
              >
                {showMap ? "List view" : "Map view"}
              </Button>
            )}
            {!isMobile && (totalUnits === availableUnitsCount ? (
              <Chip
                label={`${totalUnits} units (all available)`}
                size="small"
                color="success"
                sx={{ borderRadius: 1.5 }}
              />
            ) : (
              <>
                <Chip
                  label={`${totalUnits} Units`}
                  size="small"
                  variant="outlined"
                  sx={{ borderRadius: 1.5 }}
                />
                <Chip
                  label={`${availableUnitsCount} Available`}
                  size="small"
                  color="success"
                  sx={{ borderRadius: 1.5 }}
                />
              </>
            ))}
            {selectionMode === "floorplan" && selectedFloorPlans.size > 0 && (
              <Chip
                icon={<CheckIcon fontSize="small" />}
                label={`${selectedFloorPlans.size} Floor Plan${selectedFloorPlans.size !== 1 ? "s" : ""}`}
                size="small"
                onDelete={() => setSelectedFloorPlans(new Map())}
                sx={{
                  borderRadius: 1.5,
                  fontWeight: 600,
                  height: 24,
                  bgcolor: alpha(theme.palette.primary.main, 0.14),
                  color: theme.palette.primary.main,
                  border: "1px solid",
                  borderColor: alpha(theme.palette.primary.main, 0.28),
                  "& .MuiChip-icon": {
                    color: theme.palette.primary.main,
                    ml: 0.75,
                    mr: -0.25,
                  },
                  "& .MuiChip-deleteIcon": {
                    color: theme.palette.primary.main,
                    opacity: 0.7,
                    "&:hover": { opacity: 1, color: theme.palette.primary.main },
                  },
                }}
              />
            )}
            {selectionMode === "unit" && selectedUnits.size > 0 && (
              <Chip
                icon={<CheckIcon fontSize="small" />}
                label={`${selectedUnits.size} Unit${selectedUnits.size !== 1 ? "s" : ""}`}
                size="small"
                onDelete={() => setSelectedUnits(new Map())}
                sx={{
                  borderRadius: 1.5,
                  fontWeight: 600,
                  height: 24,
                  bgcolor: alpha(theme.palette.primary.main, 0.14),
                  color: theme.palette.primary.main,
                  border: "1px solid",
                  borderColor: alpha(theme.palette.primary.main, 0.28),
                  "& .MuiChip-icon": {
                    color: theme.palette.primary.main,
                    ml: 0.75,
                    mr: -0.25,
                  },
                  "& .MuiChip-deleteIcon": {
                    color: theme.palette.primary.main,
                    opacity: 0.7,
                    "&:hover": { opacity: 1, color: theme.palette.primary.main },
                  },
                }}
              />
            )}

            {!isMobile && canUseUnitMode && (
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={selectionMode === "unit"}
                    onChange={() => handleSwitchSelectionMode(selectionMode === "unit" ? "floorplan" : "unit")}
                  />
                }
                label={
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Select Units
                  </Typography>
                }
                sx={{ ml: 0.5, mr: 0 }}
              />
            )}

            {!isMobile && (
              <Tooltip title="Save Search">
                <Box sx={{ ml: "auto" }}>
                  <span>
                    <Button
                      size="small"
                      variant="text"
                      startIcon={<SaveIcon fontSize="small" />}
                      onClick={() => setShowSaveDialog(true)}
                      disabled={!selectedClient}
                      sx={{
                        borderRadius: 2,
                        fontWeight: 600,
                        color: "text.secondary",
                        "&:hover": {
                          backgroundColor: alpha(theme.palette.primary.main, 0.08),
                          color: "primary.main",
                        },
                      }}
                    >
                      Save Search
                    </Button>
                  </span>
                </Box>
              </Tooltip>
            )}

            <PropertyResultsSearchField
              appliedQuery={appliedPropertySearchQuery}
              onApplyQuery={setAppliedPropertySearchQuery}
              onUpdatingChange={setIsPropertyResultsUpdating}
            />
          </Box>

          {isMobile && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                mt: 1.25,
              }}
            >
              {canUseUnitMode ? (
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={selectionMode === "unit"}
                      onChange={() => handleSwitchSelectionMode(selectionMode === "unit" ? "floorplan" : "unit")}
                    />
                  }
                  label={
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Select Units
                    </Typography>
                  }
                  sx={{ ml: 0, mr: 0 }}
                />
              ) : (
                <Box />
              )}
              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  size="small"
                  variant="text"
                  onClick={handleClearAll}
                  sx={{
                    color: "text.secondary",
                    fontWeight: 500,
                    textTransform: "none",
                    minWidth: 0,
                    px: 1,
                  }}
                >
                  Clear
                </Button>
                <Tooltip title="Save Search">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => setShowSaveDialog(true)}
                      disabled={!selectedClient}
                      sx={{
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        "&:hover": {
                          backgroundColor: alpha(theme.palette.primary.main, 0.2),
                        },
                      }}
                    >
                      <SaveIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            </Box>
          )}
        </Paper>
      )}

      {!loading && groupedResults.length > 0 && isPropertyResultsUpdating && (
        <Box
          sx={{
            minHeight: 220,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            color: "text.secondary",
          }}
        >
          <CircularProgress size={28} thickness={5} />
          <Typography variant="body2" color="inherit">
            Updating properties...
          </Typography>
        </Box>
      )}

      {/* Error Alert */}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2, borderRadius: 2 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Results Map (geo "close to X" searches carry coordinates) */}
      {showMap && mapPoints.length > 0 && !loading && (
        <Box sx={{ mb: 2 }}>
          <ResultsMap points={mapPoints} height={520} />
        </Box>
      )}

      {/* Results List */}
      {(!showMap || mapPoints.length === 0) && !loading && !isPropertyResultsUpdating && filteredGroupedResults.map((property) => (
        <PropertyRow
          key={property.property_id}
          property={property}
          expanded={expandedProperties.has(property.property_id)}
          expanding={expandingPropertyId === property.property_id}
          onToggle={() => handleToggleProperty(property.property_id)}
          onExpandEntered={() => handlePropertyExpandEntered(property.property_id)}
          expandedFloorPlans={expandedFloorPlans}
          expandingFloorPlanKey={expandingFloorPlanKey}
          onToggleFloorPlan={handleToggleFloorPlan}
          onFloorPlanExpandEntered={handleFloorPlanExpandEntered}
          selectedUnits={new Set(selectedUnits.keys())}
          onToggleUnit={handleToggleUnit}
          onViewFloorPlan={handleViewFloorPlan}
          openingFloorPlanKey={openingFloorPlanKey}
          moveInDate={moveInDate}
          freshness={freshnessData.get(property.property_id) ?? null}
          freshnessLoading={freshnessLoading}
          formatFreshnessDate={formatFreshnessDate}
          isAdmin={isAdmin}
          selectionMode={selectionMode}
          selectedFloorPlans={new Set(selectedFloorPlans.keys())}
          onToggleFloorPlanSelection={handleToggleFloorPlanSelection}
        />
      ))}

      {/* Empty State — dynamic search preview.
          Gated on draftRestoreComplete so we don't flash "no results" before
          cached results hydrate from sessionStorage on tab return. */}
      {searchResults.length === 0 && !loading && draftRestoreComplete && (() => {
        const parts: string[] = [];

        // Bedrooms
        if (selectedBedrooms.length > 0) {
          const labels = selectedBedrooms
            .map((b) => (b === "0" ? "Studio" : `${b} BD`))
            .join(", ");
          parts.push(labels);
        } else {
          parts.push("Any bedrooms");
        }

        // Price
        if (priceRange[0] > PRICE_MIN || priceRange[1] < PRICE_MAX) {
          parts.push(
            `${formatPriceShort(priceRange[0])} – ${formatPriceShort(priceRange[1])}`
          );
        } else {
          parts.push("Any price");
        }

        // Neighborhoods
        if (selectedNeighborhoods.length > 0) {
          parts.push(
            selectedNeighborhoods.length <= 2
              ? selectedNeighborhoods.join(", ")
              : `${selectedNeighborhoods.length} neighborhoods`
          );
        }

        // Move-in date
        if (moveInDate) {
          parts.push(
            `Available by ${(moveInDate.getMonth() + 1)
              .toString()
              .padStart(2, "0")}/${moveInDate
              .getDate()
              .toString()
              .padStart(2, "0")}/${moveInDate.getFullYear()}`
          );
        }

        // Sqft
        if (minSqft) {
          parts.push(`${minSqft}+ sqft`);
        }

        // Amenities
        if (selectedAmenities.length > 0) {
          parts.push(
            selectedAmenities.length <= 2
              ? selectedAmenities.join(", ")
              : `${selectedAmenities.length} amenities`
          );
        }

        const hasFilters =
          selectedBedrooms.length > 0 ||
          priceRange[0] > PRICE_MIN ||
          priceRange[1] < PRICE_MAX ||
          selectedNeighborhoods.length > 0 ||
          moveInDate !== null ||
          minSqft !== "" ||
          selectedAmenities.length > 0;

        return (
          <Paper
            elevation={0}
            sx={{
              py: { xs: 2, md: 3 },
              px: { xs: 2, md: 3 },
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            {isMobile ? (
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1.5,
                  }}
                >
                  <Box sx={{ width: 50, display: "flex", justifyContent: "center" }}>
                    <Badge badgeContent={advancedFilterCount} color="primary">
                      <IconButton
                        onClick={() => setShowAdvancedFilters(true)}
                        aria-label="More Filters" title="More Filters"
                        size="small"
                        sx={{
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: 1.5,
                          width: 38,
                          height: 38,
                        }}
                      >
                        <TuneIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Badge>
                  </Box>
                  <Button
                    variant="contained"
                    onClick={handleSearch}
                    disabled={loading}
                    startIcon={
                      loading ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <SearchIcon fontSize="small" />
                      )
                    }
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "0.85rem",
                      py: 1,
                      px: 3,
                      borderRadius: 2,
                    }}
                  >
                    {loading ? "Searching..." : isMobile ? "Search" : "Search Properties"}
                  </Button>
                  <Box sx={{ width: 50, display: "flex", justifyContent: "center" }}>
                    <Button
                      size="small"
                      variant="text"
                      onClick={handleClearFilters}
                      sx={{ textTransform: "none", fontWeight: 500, minWidth: 0, px: 0.5 }}
                    >
                      Clear
                    </Button>
                  </Box>
                </Box>
              </Box>
            ) : (
              <Box sx={{ textAlign: "center" }}>
                <Button
                  variant="contained"
                  onClick={handleSearch}
                  disabled={loading}
                  startIcon={
                    loading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <SearchIcon />
                    )
                  }
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "1rem",
                    px: 5,
                    py: 1.5,
                    borderRadius: 2,
                  }}
                >
                  {loading ? "Searching..." : "Search Properties"}
                </Button>
              </Box>
            )}
            <Box sx={{ textAlign: "center", mt: 1.5 }}>
              {!hasFilters && !isMobile && (
                <Typography variant="body2" color="text.secondary">
                  Adjust filters above to narrow your search
                </Typography>
              )}
              <Typography
                variant="caption"
                color="text.secondary"
              >
                {parts.join("  ·  ")}
              </Typography>
              {selectedClient && (
                <Typography
                  variant="caption"
                  color="primary.main"
                  sx={{ display: "block", mt: 0.25 }}
                >
                  for {selectedClient.first_name} {selectedClient.last_name}
                </Typography>
              )}
            </Box>
          </Paper>
        );
      })()}

      <ShortlistComposer
        client={selectedClient}
        selections={shortlistSelections}
        onEmail={handleSendEmail}
        onNotice={(message, severity) =>
          setSnackbar({ open: true, message, severity })
        }
      />

      {/* Save Search Dialog */}
      <Dialog
        open={showSaveDialog}
        onClose={() => setShowSaveDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 600, pr: 7, position: "relative" }}>Save Search<span style={{ position: "absolute", right: 8, top: 8 }}><DesignTooltip title="Close"><span><DesignIconButton aria-label="Close" onClick={() => setShowSaveDialog(false)}  sx={{ width: 40, height: 40 }}><DesignClose sx={{ fontSize: 20 }} /></DesignIconButton></span></DesignTooltip></span></DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Save this search for {selectedClient?.first_name}{" "}
            {selectedClient?.last_name}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Search Name"
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            placeholder="e.g., 2BR under $2000"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setShowSaveDialog(false)}>Cancel</Button>
          <Button
            onClick={handleSaveSearch}
            variant="contained"
            disabled={!searchName.trim()}
            sx={{ borderRadius: 2 }}
          >
            Save Search
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floor Plan Viewer Dialog */}
      <Dialog
        open={floorPlanDialog.open}
        onClose={handleCloseFloorPlanDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 600, pr: 7, position: "relative" }}><span style={{ position: "absolute", right: 8, top: 8 }}><DesignTooltip title="Close"><span><DesignIconButton aria-label="Close" onClick={handleCloseFloorPlanDialog}  sx={{ width: 40, height: 40 }}><DesignClose sx={{ fontSize: 20 }} /></DesignIconButton></span></DesignTooltip></span>
          {floorPlanDialog.modelName} Floor Plan
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              width: "100%",
              maxHeight: "70vh",
              minHeight: 280,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {!floorPlanDialog.error ? (
              <img
                src={floorPlanDialog.imageUrl}
                alt={floorPlanDialog.modelName}
                style={{
                  maxWidth: "100%",
                  maxHeight: "65vh",
                  objectFit: "contain",
                  borderRadius: 8,
                }}
              />
            ) : (
              <Box
                sx={{
                  minHeight: 280,
                  width: "100%",
                  borderRadius: 2,
                  backgroundColor: alpha(theme.palette.primary.main, 0.06),
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                }}
              >
                <NoImageIcon sx={{ fontSize: 40, color: "text.secondary" }} />
                <Typography variant="body2" color="text.secondary">
                  Floor plan image unavailable
                </Typography>
              </Box>
            )}
          </Box>

          {/* Read-only unit details table */}
          {floorPlanDialog.units.length > 0 && (
            <TableContainer
              sx={{
                mt: 2,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: alpha(theme.palette.primary.main, 0.04),
                    }}
                  >
                    <TableCell sx={{ fontWeight: 600 }}>Unit</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Price</TableCell>
                    {!isMobile && (
                      <TableCell align="right" sx={{ fontWeight: 600 }}>$/Sq.Ft</TableCell>
                    )}
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {floorPlanDialog.units.map((unit) => {
                    const isAvailable = unit.status === "available";
                    const availDate = unit.available_date ? parseCalendarDate(unit.available_date) : null;
                    const isAvailableNow = isAvailable && availDate && availDate <= new Date();
                    return (
                      <TableRow key={unit.unique_id} sx={{ "&:last-child td": { borderBottom: 0 } }}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {unit.unit_number || "N/A"}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight={700} color="primary.main">
                            {formatPrice(unit.current_price)}
                          </Typography>
                        </TableCell>
                        {!isMobile && (
                          <TableCell align="right">
                            <Typography variant="body2" color="text.secondary">
                              {unit.price_per_sqft ? `$${unit.price_per_sqft.toFixed(2)}` : "—"}
                            </Typography>
                          </TableCell>
                        )}
                        <TableCell align="center">
                          <Chip
                            size="small"
                            label={
                              isAvailable
                                ? isAvailableNow
                                  ? "Available"
                                  : availDate
                                  ? availDate.toLocaleDateString("en-US", { month: "short", day: "numeric" })
                                  : "Available"
                                : unit.status || "Unknown"
                            }
                            color={isAvailableNow ? "success" : isAvailable ? "info" : "default"}
                            sx={{ fontWeight: 500, borderRadius: 1, height: 24, textTransform: isAvailable ? "none" : "capitalize" }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFloorPlanDialog}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Advanced Filters Drawer */}
      <Drawer
        anchor="right"
        open={showAdvancedFilters}
        onClose={() => setShowAdvancedFilters(false)}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 420 },
            p: 3,
            borderRadius: "16px 0 0 16px",
          },
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Typography variant="h6" fontWeight={700}>
            Advanced Filters
          </Typography>
          <IconButton aria-label="Close filters" title="Close filters" onClick={() => setShowAdvancedFilters(false)}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Available Units Toggle */}
        <Box mb={3}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={availableOnly}
                onChange={(e) => setAvailableOnly(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography variant="body2" fontWeight={500}>
                Available units only
              </Typography>
            }
            sx={{ "& .MuiFormControlLabel-label": { ml: 0.5 } }}
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={noFloorPlanImage}
                onChange={(e) => setNoFloorPlanImage(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography variant="body2" fontWeight={500}>
                No floor plan image
              </Typography>
            }
            sx={{ "& .MuiFormControlLabel-label": { ml: 0.5 }, mt: 1 }}
          />
        </Box>

        {/* Sort By (shown here on mobile, inline on desktop) */}
        <Box mb={3} sx={{ display: { sm: "none" } }}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Sort By
          </Typography>
          <FormControl fullWidth size="small">
            <Select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              sx={{ borderRadius: 2 }}
            >
              {sortOptions.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Neighborhoods */}
        <Box mb={3}>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            fontWeight={600}
            gutterBottom
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            <LocationIcon fontSize="small" /> Neighborhoods
          </Typography>
          {availableNeighborhoods.length > 0 ? (
            <Autocomplete
              multiple
              options={availableNeighborhoods}
              value={selectedNeighborhoods}
              onChange={(_, newValue) => setSelectedNeighborhoods(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select neighborhoods..."
                  size="small"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    size="small"
                    label={option}
                    {...getTagProps({ index })}
                    sx={{ borderRadius: 1 }}
                  />
                ))
              }
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              No neighborhoods available
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Property Management */}
        <Box mb={3}>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            fontWeight={600}
            gutterBottom
          >
            Property Management
          </Typography>
          {availableManagement.length > 0 ? (
            <Autocomplete
              multiple
              options={availableManagement}
              value={selectedManagement}
              onChange={(_, newValue) => setSelectedManagement(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select management companies..."
                  size="small"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    size="small"
                    label={option}
                    {...getTagProps({ index })}
                    sx={{ borderRadius: 1 }}
                  />
                ))
              }
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              Apply filter after search
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Min Square Feet */}
        <Box mb={3}>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            fontWeight={600}
            gutterBottom
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            <SqftIcon fontSize="small" /> Minimum Square Feet
          </Typography>
          <TextField
            fullWidth
            type="number"
            size="small"
            value={minSqft}
            onChange={(e) =>
              setMinSqft(e.target.value ? Number(e.target.value) : "")
            }
            placeholder="Any"
            sx={{
              "& .MuiOutlinedInput-root": { borderRadius: 2 },
            }}
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Amenities */}
        <Box mb={3}>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            fontWeight={600}
            gutterBottom
          >
            Amenities
          </Typography>
          <FormGroup>
            <Grid container spacing={1}>
              {amenityOptions.map((amenity) => (
                <Grid item xs={6} key={amenity.value}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedAmenities.includes(amenity.value)}
                        size="small"
                        onChange={(e) => {
                          if (e.target.checked)
                            setSelectedAmenities((prev) => [
                              ...prev,
                              amenity.value,
                            ]);
                          else
                            setSelectedAmenities((prev) =>
                              prev.filter((a) => a !== amenity.value)
                            );
                        }}
                      />
                    }
                    label={
                      <Box display="flex" alignItems="center" gap={0.5}>
                        {amenity.icon}
                        <Typography variant="body2">{amenity.label}</Typography>
                      </Box>
                    }
                    sx={{ m: 0 }}
                  />
                </Grid>
              ))}
            </Grid>
          </FormGroup>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Price Per Sq Ft */}
        <Box mb={3}>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            fontWeight={600}
            gutterBottom
          >
            Price Per Square Foot
          </Typography>
          <TextField
            fullWidth
            label="Max $/Sq.Ft"
            type="number"
            size="small"
            value={maxPricePerSqft}
            onChange={(e) =>
              setMaxPricePerSqft(e.target.value ? Number(e.target.value) : "")
            }
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">$</InputAdornment>
              ),
            }}
            placeholder="e.g., 2.50"
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        <Stack spacing={2}>
          <Button
            variant="contained"
            fullWidth
            startIcon={<SearchIcon />}
            onClick={() => {
              setShowAdvancedFilters(false);
              handleSearch();
            }}
            sx={{ borderRadius: 2, py: 1.5, fontWeight: 600 }}
          >
            Apply Filters & Search
          </Button>
          <Button
            variant="outlined"
            fullWidth
            startIcon={<ClearIcon />}
            onClick={() => {
              setSelectedNeighborhoods([]);
              setSelectedAmenities([]);
              setMaxPricePerSqft("");
              setNoFloorPlanImage(false);
            }}
            sx={{ borderRadius: 2 }}
          >
            Clear Advanced Filters
          </Button>
        </Stack>
      </Drawer>

      {/* Send Listings Dialog */}
      <SendListingsDialog
        open={sendListingsDialogOpen}
        onClose={handleCloseSendListingsDialog}
        client={selectedClient as ClientInfo | null}
        selectedListings={listingsToSend}
        revalidateListings={() => buildFreshListingSelections(
          selectionMode, selectedFloorPlans, selectedUnits, moveInDate || undefined
        )}
        selectionMode={selectionMode}
        onSuccess={handleSendListingsSuccess}
        onRemoveListing={handleRemoveListingFromSend}
        onNavigate={onNavigate}
        isDemo={isDemo}
        isAdmin={isAdmin}
        userRole={userRole}
        canToggleEmailImages={capabilities.canToggleEmailImages}
        initialDraft={sendDialogDraft ?? undefined}
        onDraftChange={setSendDialogDraft}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Saved Searches Drawer */}
      <Drawer
        anchor="right"
        open={savedSearchesOpen}
        onClose={() => setSavedSearchesOpen(false)}
        PaperProps={{
          sx: { width: { xs: "100%", sm: 400 }, p: 0 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            py: 2,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <BookmarkIcon color="primary" />
            <Typography variant="h6" fontWeight={700}>
              Saved Searches
            </Typography>
          </Box>
          <IconButton onClick={() => setSavedSearchesOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ overflow: "auto", flex: 1 }}>
          {!isDemo && !allSearchesLoaded ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress size={28} />
            </Box>
          ) : displayedSavedSearches.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 6, px: 3 }}>
              <BookmarkIcon
                sx={{ fontSize: 48, color: "text.disabled", mb: 1 }}
              />
              <Typography variant="body1" color="text.secondary" gutterBottom>
                No saved searches yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Run a search, select a client, and save it
              </Typography>
            </Box>
          ) : (() => {
            // Group by client name
            const grouped = new Map<string, SavedSearch[]>();
            displayedSavedSearches.forEach((s) => {
              const name = s.client_name || "Unknown";
              if (!grouped.has(name)) grouped.set(name, []);
              grouped.get(name)!.push(s);
            });

            return (
              <Box>
                {[...grouped.entries()].map(([clientName, searches]) => (
                  <Box key={clientName}>
                    <Box
                      sx={{
                        px: 3,
                        py: 1,
                        bgcolor: "action.hover",
                        borderBottom: 1,
                        borderColor: "divider",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <PersonIcon fontSize="small" color="action" />
                      <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                        {clientName}
                      </Typography>
                      <Chip
                        label={searches.length}
                        size="small"
                        sx={{ height: 20, fontSize: "0.7rem", ml: "auto" }}
                      />
                    </Box>
                    {searches.map((search) => {
                      const cr = search.search_criteria;
                      const parts: string[] = [];
                      if (cr.selected_bedrooms?.length) {
                        parts.push(
                          cr.selected_bedrooms
                            .map((b) => (b === "0" ? "Studio" : `${b}BD`))
                            .join(", ")
                        );
                      }
                      if (
                        cr.min_price != null &&
                        cr.max_price != null &&
                        (cr.min_price > 0 || cr.max_price < PRICE_MAX)
                      ) {
                        parts.push(
                          `${formatPriceShort(cr.min_price)} – ${formatPriceShort(cr.max_price)}`
                        );
                      }
                      if (cr.neighborhoods?.length) {
                        parts.push(
                          cr.neighborhoods.length <= 2
                            ? cr.neighborhoods.join(", ")
                            : `${cr.neighborhoods.length} areas`
                        );
                      }

                      return (
                        <Box
                          key={search.id}
                          sx={{
                            px: 3,
                            py: 1.5,
                            cursor: "pointer",
                            borderBottom: 1,
                            borderColor: "divider",
                            transition: "background 0.15s",
                            "&:hover": {
                              bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
                            },
                          }}
                          onClick={() => handleLoadSavedSearchFromDrawer(search)}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              mb: 0.25,
                            }}
                          >
                            <Typography variant="body2" fontWeight={600}>
                              {search.name}
                            </Typography>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              {!isDemo && (
                                <Typography variant="caption" color="text.disabled">
                                  {search.result_count} results
                                </Typography>
                              )}
                              {!isDemo && (
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleDeleteSavedSearch(e, search.id)}
                                  sx={{
                                    opacity: 0.4,
                                    "&:hover": { opacity: 1, color: "error.main" },
                                  }}
                                >
                                  <DeleteIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              )}
                            </Box>
                          </Box>
                          {parts.length > 0 && (
                            <Typography variant="caption" color="text.secondary">
                              {parts.join("  ·  ")}
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                ))}
              </Box>
            );
          })()}
        </Box>
      </Drawer>
    </Box>
  );
};

export default ApartmentSearch;
